import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import { HistoryProvider } from "./contexts/HistoryContext";
import { PlayerProvider } from "./contexts/PlayerContext";
import VintagePlayer from "./components/Player/VintagePlayer";
import AppCrtOverlay from "./components/Effects/AppCrtOverlay";
import ConfirmModal from "./components/ConfirmModal";
import { API_ORIGIN } from "./config/apiOrigin";
import {
  RADIO_PALETTES,
  DEFAULT_RADIO_PALETTE_ID,
  applyRadioPalette,
} from "./themes/radioPalettes";

const PALETTE_STORAGE_KEY = "radio_palette_id";
const HUB_ASSEMBLY_SESSION_KEY = "hub_assembly_completed";
const HUB_ASSEMBLY_WHEEL_THRESHOLD = 140;
const HUB_ASSEMBLY_REVEAL_COOLDOWN_MS = 650;
const RADIO_TRANSITION_FILL_MS = 600;
const RADIO_TRANSITION_HOLD_MS = 280;
const HUB_ASSEMBLY_PANEL_INDEX = {
  guide: 0,
  quick: 1,
  full: 2,
  batches: 3,
  enter: 4,
};
const HUB_ASSEMBLY_PANEL_COUNT = Object.keys(HUB_ASSEMBLY_PANEL_INDEX).length;
const ONBOARDING_WHEEL_THRESHOLD = 140;
const ONBOARDING_REVEAL_COOLDOWN_MS = 650;
const ANALYTICS_WHEEL_THRESHOLD = 140;
const ANALYTICS_REVEAL_COOLDOWN_MS = 450;
const EMPTY_BILLING_STATUS = {
  is_premium: false,
  subscription_status: null,
  premium_until: null,
  current_period_start: null,
  cancel_at: null,
  cancel_at_period_end: null,
  stripe_price_id: null,
  stripe_customer_id: null,
};

function formatImportDate(value) {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleDateString("en-US");
}

function formatRange(start, end) {
  if (!start || !end) return "Unknown range";
  return `${formatImportDate(start)} - ${formatImportDate(end)}`;
}

function formatMinutes(msValue) {
  const minutes = Math.round((Number(msValue || 0) / 1000 / 60) * 10) / 10;
  return `${minutes.toLocaleString()} min`;
}

function formatPercent(value) {
  const pct = Number(value || 0);
  return `${pct.toFixed(1)}%`;
}

function formatBillingDate(value) {
  if (!value) return "";
  const normalized = String(value).includes("T")
    ? String(value)
    : String(value).replace(" ", "T");
  const date = new Date(
    /Z$|[+-]\d{2}:\d{2}$/.test(normalized) ? normalized : `${normalized}Z`
  );
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function UtilitiesNav({
  authenticated = false,
  onLogout,
  onGetPremium,
  onManageBilling,
  onOpenSignIn,
  onOpenWaitlist,
  userProfile,
  billingStatus,
  onOpenHowTo,
  onOpenDataHub,
  onOpenLegal,
  onOpenLibrary,
  onGoHome,
  activeUtility = "",
  supportsDataHub = true,
  supportsHowTo = true,
  supportsLibrary = true,
  modeLabel = "",
}) {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileMenuRef = useRef(null);

  useEffect(() => {
    if (!isProfileOpen) return undefined;
    const handleDocumentClick = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, [isProfileOpen]);

  const profileName = String(userProfile?.display_name || "User");
  const fallbackProfileImage = `${process.env.PUBLIC_URL}/assets/default-pfp.png`;
  const profileImage = userProfile?.image_url || fallbackProfileImage;
  const isPremiumUser = Boolean(billingStatus?.is_premium);
  const subscriptionStatus = billingStatus?.subscription_status || "";
  const currentPeriodStart = formatBillingDate(billingStatus?.current_period_start);
  const periodEnd = formatBillingDate(billingStatus?.premium_until);
  const cancelAt = formatBillingDate(billingStatus?.cancel_at);
  const cancelAtPeriodEnd = Boolean(billingStatus?.cancel_at_period_end);
  const subscriptionDateText = currentPeriodStart || "Not available";
  const renewalLabel = cancelAtPeriodEnd ? "Ends on" : "Renews on";
  const renewalDate = periodEnd || cancelAt || "";
  const nextBillingText = renewalDate || "Not available";

  return (
    <nav className="utilities-nav" aria-label="Utilities">
      <div className="utilities-nav-left">
        <button
          type="button"
          className={`utilities-nav-title ${
            activeUtility === "home" ? "utilities-nav-title--active" : ""
          }`}
          onClick={onGoHome}
          disabled={!onGoHome}
        >
          RETROFY
        </button>

        {supportsDataHub && (
          <button
            type="button"
            className={`utility-chip ${
              activeUtility === "hub" ? "utility-chip--active" : ""
            }`}
            onClick={() => {
              if (!authenticated) return;
              onOpenDataHub?.();
            }}
          >
            SPOTIFY HUB
          </button>
        )}
        {supportsLibrary && (
          <button
            type="button"
            className={`utility-chip ${
              activeUtility === "library" ? "utility-chip--active" : ""
            }`}
            onClick={() => {
              if (!authenticated) return;
              onOpenLibrary?.();
            }}
          >
            LOCAL LIBRARY
          </button>
        )}
        {supportsHowTo && (
          <button
            type="button"
            className={`utility-chip ${
              activeUtility === "howto" ? "utility-chip--active" : ""
            }`}
            onClick={() => {
              if (!authenticated) return;
              onOpenHowTo?.();
            }}
          >
            HOW TO
          </button>
        )}
      </div>
      <div className="utilities-nav-actions">
        {authenticated ? (
          <>
            {modeLabel ? (
              <div className="nav-mode-pill" aria-label={`Current mode: ${modeLabel}`}>
                {modeLabel}
              </div>
            ) : null}
            <button
              type="button"
              className={`premium-btn ${isPremiumUser ? "premium-btn--owned" : ""}`}
              onClick={onGetPremium}
              disabled={isPremiumUser || !onGetPremium}
            >
              {isPremiumUser ? "PREMIUM" : "GET PREMIUM"}
            </button>
            <div className="nav-profile-wrap" ref={profileMenuRef}>
              <button
                type="button"
                className="nav-profile-btn"
                onClick={() => setIsProfileOpen((open) => !open)}
                aria-haspopup="dialog"
                aria-expanded={isProfileOpen}
                aria-label="Profile"
              >
                <img
                  src={profileImage}
                  alt=""
                  className="nav-profile-avatar"
                  onError={(event) => {
                    if (event.currentTarget.src !== fallbackProfileImage) {
                      event.currentTarget.src = fallbackProfileImage;
                    }
                  }}
                />
              </button>
              {isProfileOpen && (
                <div className="nav-profile-popover" role="dialog" aria-label="Profile summary">
                  <div className="nav-profile-name">{profileName}</div>
                  {isPremiumUser && <div className="nav-profile-tier">Premium</div>}
                  {isPremiumUser && (
                    <>
                      {subscriptionStatus && (
                        <div className="nav-profile-meta">
                          Status: {String(subscriptionStatus).replaceAll("_", " ")}
                        </div>
                      )}
                      {periodEnd && (
                        <div className="nav-profile-meta">
                          {renewalLabel}: {nextBillingText}
                        </div>
                      )}
                      <div className="nav-profile-meta">
                        Subscription start: {subscriptionDateText}
                      </div>
                      {!periodEnd && (
                        <div className="nav-profile-meta">
                          {renewalLabel}: {nextBillingText}
                        </div>
                      )}
                      <button
                        type="button"
                        className="nav-profile-manage-billing"
                        onClick={() => {
                          setIsProfileOpen(false);
                          onManageBilling?.();
                        }}
                        disabled={!onManageBilling}
                      >
                        MANAGE BILLING
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className="nav-profile-logout"
                    onClick={() => {
                      setIsProfileOpen(false);
                      onLogout?.();
                    }}
                  >
                    LOG OUT
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <button
            type="button"
            className="btn-spotify btn-spotify--nav utilities-nav-action"
            onClick={() => {
              if (onOpenSignIn) {
                onOpenSignIn();
                return;
              }
              onOpenWaitlist?.();
            }}
          >
            SIGN IN
          </button>
        )}
      </div>
    </nav>
  );
}

function SiteLegalFooter({ onOpenLegal }) {
  return (
    <footer className="site-legal-footer" aria-label="Legal links">
      <button type="button" className="site-legal-link" onClick={() => onOpenLegal?.()}>
        Privacy & Terms
      </button>
      <span className="site-legal-sep">•</span>
      <a className="site-legal-link" href="mailto:support@retrofy.app">
        Contact
      </a>
      <span className="site-legal-sep">•</span>
      <span className="site-legal-copy-mini">© 2026 Retrofy</span>
    </footer>
  );
}

function WaitlistModal({
  isOpen,
  email,
  displayName,
  note,
  onChangeEmail,
  onChangeDisplayName,
  onChangeNote,
  onSubmit,
  onClose,
  onContinueSpotifyLogin,
  isSubmitting = false,
  error = "",
  success = "",
}) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="confirm-modal-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        className="confirm-modal confirm-modal--warning waitlist-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Waitlist signup"
      >
        <h2 className="confirm-modal-title">SPOTIFY ACCESS WAITLIST</h2>
        <p className="confirm-modal-message waitlist-modal-copy">
          Name and Spotify email are required. We only use this information to
          review access requests and notify you when your Spotify access is approved.
        </p>
        <form
          className="waitlist-form"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit?.();
          }}
        >
          <input
            type="text"
            className="waitlist-input"
            placeholder="Full name (required)"
            value={displayName}
            onChange={(event) => onChangeDisplayName?.(event.target.value)}
            required
            maxLength={255}
            autoFocus
          />
          <input
            type="email"
            className="waitlist-input"
            placeholder="Spotify account email (required)"
            value={email}
            onChange={(event) => onChangeEmail?.(event.target.value)}
            required
            maxLength={320}
          />
          <textarea
            className="waitlist-textarea"
            placeholder="Notes (optional)"
            value={note}
            onChange={(event) => onChangeNote?.(event.target.value)}
            maxLength={500}
            rows={3}
          />
          {error ? <div className="waitlist-error">{error}</div> : null}
          {success ? <div className="waitlist-success">{success}</div> : null}
          <div className="waitlist-actions">
            <button
              type="button"
              className="confirm-modal-btn confirm-modal-btn--cancel"
              onClick={() => onClose?.()}
              disabled={isSubmitting}
            >
              CLOSE
            </button>
            <button
              type="submit"
              className="confirm-modal-btn confirm-modal-btn--confirm"
              disabled={isSubmitting}
            >
              {isSubmitting ? "SUBMITTING..." : "JOIN WAITLIST"}
            </button>
          </div>
        </form>
        <button
          type="button"
          className="waitlist-approved-link"
          onClick={() => onContinueSpotifyLogin?.()}
          disabled={isSubmitting}
        >
          Already approved? Connect Spotify now
        </button>
      </div>
    </div>
  );
}

function Login({
  onOpenWaitlist,
  onContinueSpotifyLogin,
}) {
  const [showSpotifyApprovalModal, setShowSpotifyApprovalModal] = useState(false);

  return (
    <div className="login-page">
      <div className="login-shell">
        <div className="login-auth">
          <div className="login-auth-panel">
            <div className="login-brand">RETROFY</div>
            <div className="login-headline">Sign in to continue</div>
            <div className="login-subtitle">
              Connect your Spotify account to continue.
            </div>
            <button
              type="button"
              className="btn-spotify login-auth-spotify-btn"
              onClick={() => setShowSpotifyApprovalModal(true)}
            >
              <img
                src={`${process.env.PUBLIC_URL}/assets/spotify-icon.webp`}
                alt=""
                className="login-auth-spotify-icon"
              />
              <span className="btn-spotify__label">Continue with Spotify</span>
            </button>
          </div>
        </div>
        <ConfirmModal
          isOpen={showSpotifyApprovalModal}
          title="SPOTIFY APPROVAL"
          message="Spotify access is currently approved manually. Join waitlist if you are not approved yet, or continue if you already have access."
          confirmText="CONTINUE SPOTIFY"
          cancelText="JOIN WAITLIST"
          onConfirm={() => {
            setShowSpotifyApprovalModal(false);
            onContinueSpotifyLogin?.();
          }}
          onCancel={() => {
            setShowSpotifyApprovalModal(false);
            onOpenWaitlist?.();
          }}
          showCancel
          variant="warning"
        />
      </div>
    </div>
  );
}

function HomeLanding({ onOpenSignIn }) {
  return (
    <div className="login-page">
      <div className="login-shell">
        <div className="login-home">
          <section className="login-hero login-hero--video-first">
            <div className="login-hero-visual login-hero-visual--large">
              <video className="login-demo-video" autoPlay muted loop>
                <source
                  src={`${process.env.PUBLIC_URL}/assets/demo-video.mp4`}
                  type="video/mp4"
                />
              </video>
            </div>
            <div className="login-panel">
              <div className="login-brand">RETROFY</div>
              <div className="login-headline">
                Turn your lifetime listening history into a playable time machine.
              </div>
              <div className="login-subtitle">
                Explore lifetime trends, jump into yearly hits, and relive your
                eras through a retro UI built for deep music analytics.
              </div>
              <div className="login-proof-row">
                <div className="login-proof-chip">LIFETIME LISTENING ANALYTICS</div>
                <div className="login-proof-chip">YEARLY HITS + ERA DISCOVERY</div>
                <div className="login-proof-chip">RETRO TIME-TRAVEL PLAYER</div>
                <div className="login-proof-chip">BATCH INSIGHTS + STREAKS</div>
              </div>
            </div>
          </section>
          <section className="login-final-cta">
            <button
              type="button"
              className="mode-btn mode-btn--hero pixel-glow-btn"
              onClick={() => onOpenSignIn?.()}
            >
              <span className="btn-spotify__label">Get Started</span>
              <span className="btn-spotify__pixel-burst" aria-hidden="true">
                <span className="btn-spotify__pixel" />
                <span className="btn-spotify__pixel" />
                <span className="btn-spotify__pixel" />
                <span className="btn-spotify__pixel" />
                <span className="btn-spotify__pixel" />
                <span className="btn-spotify__pixel" />
                <span className="btn-spotify__pixel" />
                <span className="btn-spotify__pixel" />
                <span className="btn-spotify__pixel" />
                <span className="btn-spotify__pixel" />
                <span className="btn-spotify__pixel" />
                <span className="btn-spotify__pixel" />
              </span>
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}

function LocalLibraryEditor({ onBack, onLaunchRadio }) {
  const [libraries, setLibraries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingPlaylistId, setDeletingPlaylistId] = useState("");
  const [renamingPlaylistId, setRenamingPlaylistId] = useState("");
  const [activeEditPlaylistId, setActiveEditPlaylistId] = useState("");
  const [editingPlaylistId, setEditingPlaylistId] = useState("");
  const [editingPlaylistName, setEditingPlaylistName] = useState("");
  const [deletingTrackId, setDeletingTrackId] = useState("");
  const [expandedPlaylistIds, setExpandedPlaylistIds] = useState([]);
  const [pendingDeletePlaylist, setPendingDeletePlaylist] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [playlistName, setPlaylistName] = useState("");
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [dropActive, setDropActive] = useState(false);
  const fileInputRef = useRef(null);

  const refreshServerLibraries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/local-library/tracks`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch local libraries");
      const payload = await response.json();
      const nextLibraries = Array.isArray(payload?.libraries) ? payload.libraries : [];
      setLibraries(nextLibraries);
    } catch {
      setLibraries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshServerLibraries();
  }, [refreshServerLibraries]);

  useEffect(() => {
    const currentIds = new Set(libraries.map((library) => library.id));
    setExpandedPlaylistIds((prev) => prev.filter((id) => currentIds.has(id)));
    setActiveEditPlaylistId((prev) => (prev && !currentIds.has(prev) ? "" : prev));
    setEditingPlaylistId((prev) => (prev && !currentIds.has(prev) ? "" : prev));
  }, [libraries]);

  const handleUploadFiles = useCallback((files) => {
    const next = Array.from(files || []).filter(Boolean);
    setUploadFiles(next);
  }, []);

  const handleUploadSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!playlistName.trim()) {
        setUploadError("Playlist name is required.");
        return;
      }
      if (uploadFiles.length === 0) {
        setUploadError("Select at least one audio file.");
        return;
      }

      const formData = new FormData();
      formData.append("playlistName", playlistName.trim());
      uploadFiles.forEach((file) => formData.append("audioFiles", file));

      setUploading(true);
      setUploadProgress(0);
      setUploadPhase("Uploading files...");
      setUploadSuccess("");
      setUploadError("");
      try {
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("POST", `${API_ORIGIN}/api/local-library/upload`);
          xhr.withCredentials = true;

          xhr.upload.onprogress = (progressEvent) => {
            if (!progressEvent.lengthComputable) return;
            const fraction = progressEvent.loaded / progressEvent.total;
            const next = Math.max(6, Math.min(92, Math.round(fraction * 92)));
            setUploadProgress(next);
            setUploadPhase("Uploading files...");
          };

          xhr.onload = () => {
            if (xhr.status < 200 || xhr.status >= 300) {
              try {
                const payload = JSON.parse(xhr.responseText || "{}");
                reject(new Error(payload?.error || "Upload failed"));
              } catch {
                reject(new Error("Upload failed"));
              }
              return;
            }
            try {
              resolve(JSON.parse(xhr.responseText || "{}"));
            } catch {
              resolve({});
            }
          };

          xhr.onerror = () => reject(new Error("Network error"));
          xhr.send(formData);
        });
        setUploadProgress(100);
        setUploadPhase("Completed");
        setUploadFiles([]);
        setPlaylistName("");
        if (fileInputRef.current) fileInputRef.current.value = "";
        await refreshServerLibraries();
        setUploadSuccess("Upload complete.");
        window.setTimeout(() => setUploadSuccess(""), 2200);
      } catch (error) {
        setUploadError(String(error?.message || "Upload failed."));
        setUploadProgress(0);
        setUploadPhase("");
      } finally {
        setUploading(false);
      }
    },
    [playlistName, refreshServerLibraries, uploadFiles]
  );

  const handleDeletePlaylistRequest = useCallback((playlist) => {
    setPendingDeletePlaylist(playlist);
    setShowDeleteConfirm(true);
  }, []);

  const handleDeletePlaylistConfirm = useCallback(
    async (playlistId) => {
      if (!playlistId || deletingPlaylistId) return;
      setDeletingPlaylistId(playlistId);
      setShowDeleteConfirm(false);
      try {
        const response = await fetch(
          `${API_ORIGIN}/api/local-library/playlists/${encodeURIComponent(playlistId)}`,
          {
            method: "DELETE",
            credentials: "include",
          }
        );
        if (!response.ok) throw new Error("Delete failed");
        await refreshServerLibraries();
      } catch {
        setUploadError("Could not delete playlist right now.");
      } finally {
        setDeletingPlaylistId("");
        setPendingDeletePlaylist(null);
      }
    },
    [deletingPlaylistId, refreshServerLibraries]
  );

  const handleTogglePlaylistExpanded = useCallback((playlistId) => {
    setExpandedPlaylistIds((prev) =>
      prev.includes(playlistId)
        ? prev.filter((id) => id !== playlistId)
        : [...prev, playlistId]
    );
  }, []);

  const beginRenamePlaylist = useCallback((playlist) => {
    const playlistId = String(playlist?.id || "").trim();
    if (!playlistId || deletingPlaylistId || renamingPlaylistId) return;
    if (activeEditPlaylistId !== playlistId) return;
    setEditingPlaylistId(playlistId);
    setEditingPlaylistName(String(playlist?.title || ""));
    setUploadError("");
  }, [activeEditPlaylistId, deletingPlaylistId, renamingPlaylistId]);

  const cancelRenamePlaylist = useCallback(() => {
    setEditingPlaylistId("");
    setEditingPlaylistName("");
  }, []);

  const togglePlaylistEditMode = useCallback(
    (playlistId) => {
      const normalizedPlaylistId = String(playlistId || "").trim();
      if (!normalizedPlaylistId || deletingPlaylistId || renamingPlaylistId) return;
      setUploadError("");
      setActiveEditPlaylistId((prev) => {
        const shouldClose = prev === normalizedPlaylistId;
        if (shouldClose || (editingPlaylistId && editingPlaylistId !== normalizedPlaylistId)) {
          cancelRenamePlaylist();
        }
        return shouldClose ? "" : normalizedPlaylistId;
      });
    },
    [cancelRenamePlaylist, deletingPlaylistId, editingPlaylistId, renamingPlaylistId]
  );

  const submitRenamePlaylist = useCallback(async () => {
    const playlistId = String(editingPlaylistId || "").trim();
    const proposedName = String(editingPlaylistName || "").trim();
    if (!playlistId) return;
    if (!proposedName) {
      setUploadError("Playlist name cannot be empty.");
      return;
    }
    setRenamingPlaylistId(playlistId);
    try {
      const response = await fetch(
        `${API_ORIGIN}/api/local-library/playlists/${encodeURIComponent(playlistId)}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ name: proposedName }),
        }
      );
      if (!response.ok) throw new Error("Playlist rename failed");
      await refreshServerLibraries();
      cancelRenamePlaylist();
    } catch {
      setUploadError("Could not rename playlist right now.");
    } finally {
      setRenamingPlaylistId("");
    }
  }, [editingPlaylistId, editingPlaylistName, refreshServerLibraries, cancelRenamePlaylist]);

  const handleDeleteTrack = useCallback(
    async (playlistId, relativePath) => {
      const normalizedPlaylistId = String(playlistId || "").trim();
      const normalizedTrackPath = String(relativePath || "").trim();
      if (!normalizedPlaylistId || !normalizedTrackPath || deletingTrackId) return;
      const requestId = `${normalizedPlaylistId}:${normalizedTrackPath}`;
      setDeletingTrackId(requestId);
      try {
        const response = await fetch(
          `${API_ORIGIN}/api/local-library/playlists/${encodeURIComponent(normalizedPlaylistId)}/tracks`,
          {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ relative_path: normalizedTrackPath }),
          }
        );
        if (!response.ok) throw new Error("Track delete failed");
        await refreshServerLibraries();
      } catch {
        setUploadError("Could not delete track right now.");
      } finally {
        setDeletingTrackId("");
      }
    },
    [deletingTrackId, refreshServerLibraries]
  );

  return (
    <div className="mode-shell">
      <div className="mode-panel premium-panel local-hub-panel">
        <div className="mode-header">
          <div className="mode-title">LOCAL LIBRARY</div>
          <button
            type="button"
            className="mode-btn mode-btn--small analytics-back-btn"
            onClick={onBack}
          >
            Back
          </button>
        </div>
        <div className="mode-subtitle mode-subtitle--compact">
          Upload playlists!
        </div>
        <div className="local-hub-grid local-hub-grid--single">
          <section className="legal-block local-hub-card">
            <div className="generated-playlists-head">
              <div className="generated-playlists-title">UPLOAD PLAYLIST</div>
            </div>
            <form className="upload-form upload-form--inline local-upload-form" onSubmit={handleUploadSubmit}>
              <input
                type="text"
                className="waitlist-input local-playlist-input"
                placeholder="Playlist folder name"
                value={playlistName}
                onChange={(event) => setPlaylistName(event.target.value)}
                maxLength={80}
              />
              <label
                className={`upload-dropzone local-upload-dropzone ${
                  dropActive ? "local-upload-dropzone--active" : ""
                }`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDropActive(true);
                }}
                onDragLeave={() => setDropActive(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setDropActive(false);
                  handleUploadFiles(event.dataTransfer?.files || []);
                }}
              >
                <span className="upload-dropzone-label">DROPBOX</span>
                <span className="upload-file-trigger">CHOOSE AUDIO FILES</span>
                <span className="upload-file-hint">
                  {uploadFiles.length > 0
                    ? `${uploadFiles.length} file(s) selected`
                    : "Drag and drop .mp3/.m4a/.wav/.ogg/.flac/.aac"}
                </span>
                <input
                  ref={fileInputRef}
                  className="upload-file-input"
                  type="file"
                  multiple
                  accept="audio/*,.mp3,.m4a,.wav,.ogg,.flac,.aac"
                  onChange={(event) => handleUploadFiles(event.target.files || [])}
                />
              </label>
              <div className="upload-card-action">
                <button
                  type="submit"
                  className="mode-btn mode-btn--small mode-btn--timetravel"
                  disabled={uploading}
                >
                  {uploading ? "Uploading..." : "Upload Playlist"}
                </button>
              </div>
              {uploading && (
                <div className="upload-progress-wrap">
                  <div className="upload-progress-label">{uploadPhase || "Uploading..."}</div>
                  <div
                    className="upload-progress-track"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={uploadProgress}
                  >
                    <div
                      className="upload-progress-fill"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <div className="upload-progress-value">{uploadProgress}%</div>
                </div>
              )}
              {uploadSuccess ? (
                <div className="waitlist-success local-upload-success">{uploadSuccess}</div>
              ) : null}
              {uploadError ? <div className="upload-error">{uploadError}</div> : null}
            </form>
          </section>

          <section className="legal-block local-hub-card">
            <div className="generated-playlists-head">
              <div className="generated-playlists-title">PLAYLISTS</div>
            </div>
            <div className="generated-playlists-list local-playlists-list">
              {loading ? (
                <div className="generated-playlists generated-playlists--empty">Loading playlists...</div>
              ) : libraries.length === 0 ? (
                <div className="generated-playlists generated-playlists--empty">
                  No playlists found in `data/local-audio/mp3`.
                </div>
              ) : (
                libraries.map((library) => (
                  <div key={library.id} className="generated-playlist-item generated-playlist-item--group">
                    <div className="generated-playlist-toprow">
                      <div className="generated-playlist-main">
                        {editingPlaylistId === library.id ? (
                          <input
                            type="text"
                            className="generated-playlist-name-input"
                            value={editingPlaylistName}
                            onChange={(event) => setEditingPlaylistName(event.target.value)}
                            onClick={(event) => event.stopPropagation()}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                submitRenamePlaylist();
                              }
                              if (event.key === "Escape") {
                                event.preventDefault();
                                cancelRenamePlaylist();
                              }
                            }}
                            onBlur={() => {
                              if (!renamingPlaylistId) {
                                submitRenamePlaylist();
                              }
                            }}
                            autoFocus
                            maxLength={80}
                          />
                        ) : (
                          activeEditPlaylistId === library.id ? (
                            <button
                              type="button"
                              className="generated-playlist-title-btn"
                              onClick={() => beginRenamePlaylist(library)}
                            >
                              {library.title}
                            </button>
                          ) : (
                            <div className="generated-playlist-title-label">{library.title}</div>
                          )
                        )}
                      </div>
                      <div className="generated-playlist-actions">
                        {!library?.readonly ? (
                          <button
                            type="button"
                            className="generated-playlist-edit"
                            onClick={(event) => {
                              event.stopPropagation();
                              togglePlaylistEditMode(library.id);
                            }}
                          >
                            {activeEditPlaylistId === library.id ? "DONE" : "EDIT"}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="generated-playlist-expand"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleTogglePlaylistExpanded(library.id);
                          }}
                          aria-label={expandedPlaylistIds.includes(library.id) ? "Collapse playlist" : "Expand playlist"}
                        >
                          <span
                            className={`generated-playlist-expand-icon ${
                              expandedPlaylistIds.includes(library.id) ? "is-open" : ""
                            }`}
                            aria-hidden="true"
                          >
                            ❯
                          </span>
                        </button>
                        {!library?.readonly && activeEditPlaylistId === library.id ? (
                          <button
                            type="button"
                            className="generated-playlist-delete"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleDeletePlaylistRequest(library);
                            }}
                            disabled={deletingPlaylistId === library.id}
                          >
                            {deletingPlaylistId === library.id ? "..." : "DELETE"}
                          </button>
                        ) : null}
                      </div>
                    </div>
                    <div
                      className={`local-library-expand-panel ${
                        expandedPlaylistIds.includes(library.id) ? "is-open" : ""
                      }`}
                    >
                      <div className="local-library-tracklist--editor">
                        {(library.tracks || []).length === 0 ? (
                          <div className="local-library-empty-hint">No songs in this playlist.</div>
                        ) : (
                          (library.tracks || []).map((track) => {
                            const trackDeleteId = `${library.id}:${track.relative_path}`;
                            return (
                              <div
                                key={track.id || track.relative_path}
                                className={`local-library-track-row--editor ${
                                  activeEditPlaylistId === library.id ? "" : "is-readonly"
                                }`}
                              >
                                <button type="button" className="local-editor-track-btn">
                                  🎵 {track.name || "Unknown Track"}
                                </button>
                                {!track?.readonly && !library?.readonly && activeEditPlaylistId === library.id ? (
                                  <button
                                    type="button"
                                    className="local-library-remove-btn"
                                    onClick={() => handleDeleteTrack(library.id, track.relative_path)}
                                    disabled={deletingTrackId === trackDeleteId}
                                    aria-label={`Delete ${track.name || "track"}`}
                                  >
                                    {deletingTrackId === trackDeleteId ? "…" : "×"}
                                  </button>
                                ) : null}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>
        <div className="hub-enter-radio-wrap local-hub-launch-wrap">
          <button
            type="button"
            className="mode-btn mode-btn--hero pixel-glow-btn"
            onClick={onLaunchRadio}
          >
            <span className="btn-spotify__label">Launch</span>
            <span className="btn-spotify__pixel-burst" aria-hidden="true">
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
            </span>
          </button>
        </div>
      </div>
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="DELETE PLAYLIST"
        message={
          pendingDeletePlaylist
            ? `Delete "${pendingDeletePlaylist.title}" and all audio files inside it?`
            : "Delete this playlist?"
        }
        confirmText="DELETE"
        cancelText="CANCEL"
        onConfirm={() => handleDeletePlaylistConfirm(pendingDeletePlaylist?.id || "")}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setPendingDeletePlaylist(null);
        }}
        variant="warning"
        showCancel
      />
    </div>
  );
}

function DataHub({
  historyMode,
  hasUploadedHistory,
  modeLoading,
  onSelectMode,
  onUploadComplete,
  onEnterRadio,
  onSelectImportBatch,
  onDeleteImportBatch,
  onRenameImportBatch,
  onOpenBatchAnalytics,
  importBatches,
  playlistsLoading,
  selectedImportId,
}) {
  const initialAssemblyCompleted =
    typeof window !== "undefined" &&
    window.sessionStorage.getItem(HUB_ASSEMBLY_SESSION_KEY) === "1";
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState("");
  const [error, setError] = useState("");
  const [deletingBatchId, setDeletingBatchId] = useState(null);
  const [deleteProgress, setDeleteProgress] = useState(0);
  const [editingBatchId, setEditingBatchId] = useState(null);
  const [editingBatchName, setEditingBatchName] = useState("");
  const [showBatchWarning, setShowBatchWarning] = useState(false);
  const [batchSearchQuery, setBatchSearchQuery] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [batchToDelete, setBatchToDelete] = useState(null);
  const [hasCompletedAssembly, setHasCompletedAssembly] = useState(
    initialAssemblyCompleted
  );
  const [revealedPanelCount, setRevealedPanelCount] = useState(
    initialAssemblyCompleted ? HUB_ASSEMBLY_PANEL_COUNT : 0
  );
  const wheelDeltaAccumulatorRef = useRef(0);
  const lastRevealTimeRef = useRef(0);
  const isYearlyHitsSelected = historyMode === "yearly-hits";
  const isFullSelected = historyMode === "time-travel-ready";
  const canEnterRadio =
    isYearlyHitsSelected || (isFullSelected && hasUploadedHistory);
  const fullHistoryGroups = hasUploadedHistory ? importBatches : [];
  const selectedFileCount = files.length;
  const selectedBatch =
    importBatches.find((batch) => batch.id === selectedImportId) || null;
  const showScrollPrompt =
    !hasCompletedAssembly && revealedPanelCount < HUB_ASSEMBLY_PANEL_COUNT;
  const normalizedBatchSearchQuery = batchSearchQuery.trim().toLowerCase();
  const filteredHistoryGroups = normalizedBatchSearchQuery
    ? fullHistoryGroups.filter((group) => {
        const batchName = (
          group.custom_name || `Batch #${group.id}`
        ).toLowerCase();
        return batchName.includes(normalizedBatchSearchQuery);
      })
    : fullHistoryGroups;

  useEffect(() => {
    if (!isFullSelected) {
      setError("");
      setShowBatchWarning(false);
    }
  }, [isFullSelected]);

  useEffect(() => {
    if (hasCompletedAssembly) return undefined;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const isTouchLikeDevice =
      window.matchMedia("(pointer: coarse)").matches ||
      window.matchMedia("(hover: none)").matches ||
      window.matchMedia("(max-width: 980px)").matches;

    if (prefersReducedMotion || isTouchLikeDevice) {
      setHasCompletedAssembly(true);
      setRevealedPanelCount(HUB_ASSEMBLY_PANEL_COUNT);
      window.sessionStorage.setItem(HUB_ASSEMBLY_SESSION_KEY, "1");
      return undefined;
    }

    const handleWheel = (event) => {
      if (revealedPanelCount >= HUB_ASSEMBLY_PANEL_COUNT) return;
      if (event.defaultPrevented) return;

      const now = Date.now();
      if (now - lastRevealTimeRef.current < HUB_ASSEMBLY_REVEAL_COOLDOWN_MS)
        return;

      wheelDeltaAccumulatorRef.current = Math.max(
        0,
        wheelDeltaAccumulatorRef.current + event.deltaY
      );
      if (wheelDeltaAccumulatorRef.current < HUB_ASSEMBLY_WHEEL_THRESHOLD)
        return;

      wheelDeltaAccumulatorRef.current = 0;
      lastRevealTimeRef.current = now;
      setRevealedPanelCount((prev) =>
        Math.min(prev + 1, HUB_ASSEMBLY_PANEL_COUNT)
      );
      event.preventDefault();
    };

    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      window.removeEventListener("wheel", handleWheel);
    };
  }, [hasCompletedAssembly, revealedPanelCount]);

  useEffect(() => {
    if (hasCompletedAssembly) return;
    if (revealedPanelCount < HUB_ASSEMBLY_PANEL_COUNT) return;
    setHasCompletedAssembly(true);
    window.sessionStorage.setItem(HUB_ASSEMBLY_SESSION_KEY, "1");
  }, [hasCompletedAssembly, revealedPanelCount]);

  const isPanelVisible = (panelKey) =>
    revealedPanelCount > HUB_ASSEMBLY_PANEL_INDEX[panelKey];

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!files.length) {
      setError("Select one or more Spotify JSON files first.");
      return;
    }
    if (!isFullSelected) {
      try {
        await onSelectMode?.("time-travel");
      } catch (_error) {
        // continue upload attempt; backend upload path is mode-agnostic
      }
    }

    const formData = new FormData();
    files.forEach((file) => formData.append("historyFiles", file));

    setIsUploading(true);
    setUploadProgress(0);
    setUploadPhase("Uploading files...");
    setError("");

    let processingTimer = null;

    try {
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API_ORIGIN}/api/history/upload`);
        xhr.withCredentials = true;

        xhr.upload.onprogress = (progressEvent) => {
          if (!progressEvent.lengthComputable) return;
          const fraction = progressEvent.loaded / progressEvent.total;
          const next = Math.max(3, Math.min(55, Math.round(fraction * 55)));
          setUploadProgress(next);
          setUploadPhase("Uploading files...");
        };

        xhr.upload.onload = () => {
          setUploadProgress((prev) => Math.max(prev, 55));
          setUploadPhase("Processing...");
          processingTimer = window.setInterval(() => {
            setUploadProgress((prev) => {
              if (prev >= 92) return prev;
              return prev + 1;
            });
          }, 180);
        };

        xhr.onload = () => {
          if (processingTimer) window.clearInterval(processingTimer);
          if (xhr.status < 200 || xhr.status >= 300) {
            try {
              const errorResponse = JSON.parse(xhr.responseText || "{}");
              reject(new Error(JSON.stringify(errorResponse)));
            } catch {
              reject(new Error("Upload failed"));
            }
            return;
          }
          try {
            resolve(JSON.parse(xhr.responseText || "{}"));
          } catch {
            reject(new Error("Invalid upload response"));
          }
        };

        xhr.onerror = () => {
          if (processingTimer) window.clearInterval(processingTimer);
          reject(new Error("Network error"));
        };

        xhr.send(formData);
      });
      setUploadProgress(100);
      setUploadPhase("Completed");
      onUploadComplete(data);
    } catch (uploadError) {
      // Parse error to extract details
      let errorMessage = "Could not process uploaded files.";
      let errorDetails = "";

      try {
        const errorData = JSON.parse(uploadError.message);
        if (errorData.code === 'INVALID_JSON') {
          errorMessage = errorData.message || "Invalid JSON in uploaded file";
          errorDetails = errorData.details || "";
        } else if (errorData.code === 'MISSING_SPOTIFY_FIELDS') {
          errorMessage = "File is not from Spotify Listening History";
          errorDetails = errorData.details || "This might be from Apple Music, YouTube Music, or another service.";
        } else if (errorData.code === 'INVALID_FORMAT') {
          errorMessage = "File format is incorrect";
          errorDetails = errorData.details || "Expected a JSON array of streaming history entries.";
        } else if (errorData.code === 'EMPTY_ARRAY') {
          errorMessage = "The JSON file is empty";
          errorDetails = "Please check that you uploaded the correct file.";
        } else if (errorData.code === 'NO_VALID_ENTRIES') {
          errorMessage = "No valid listening entries found";
          errorDetails = errorData.details || "The file may be corrupted or in an unexpected format.";
        } else if (errorData.error) {
          errorMessage = errorData.error;
          errorDetails = errorData.details || (errorData.failedFiles ? `${errorData.failedFiles.length} file(s) failed to process` : "");
        }
      } catch {
        errorMessage = uploadError.message || "Could not process uploaded files. Please verify they are Spotify JSON history files.";
      }

      const fullError = errorDetails ? `${errorMessage}\n\n${errorDetails}` : errorMessage;
      setError(fullError);
      setUploadProgress(0);
      setUploadPhase("");
    } finally {
      if (processingTimer) window.clearInterval(processingTimer);
      setIsUploading(false);
    }
  };

  const handleDeleteBatch = async (event, batchId) => {
    event.stopPropagation();
    if (deletingBatchId) return;
    setBatchToDelete(batchId);
    setShowDeleteConfirm(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setBatchToDelete(null);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteConfirm(false);
    if (!batchToDelete) return;

    setDeletingBatchId(batchToDelete);
    setDeleteProgress(8);
    const timer = window.setInterval(() => {
      setDeleteProgress((prev) => {
        if (prev >= 88) return prev;
        return prev + 4;
      });
    }, 160);

    try {
      await onDeleteImportBatch(batchToDelete);
      setDeleteProgress(100);
    } catch (deleteError) {
      setError("Failed to delete batch. Please try again.");
    } finally {
      window.clearInterval(timer);
      window.setTimeout(() => {
        setDeletingBatchId(null);
        setDeleteProgress(0);
        setBatchToDelete(null);
      }, 220);
    }
  };

  const startEditingBatch = (event, batch) => {
    event.stopPropagation();
    event.preventDefault();
    if (deletingBatchId) return;
    setEditingBatchId(batch.id);
    setEditingBatchName(batch.custom_name || `Batch #${batch.id}`);
  };

  const cancelEditingBatch = () => {
    setEditingBatchId(null);
    setEditingBatchName("");
  };

  const saveBatchName = async (event, batchId) => {
    event?.stopPropagation?.();
    const trimmed = editingBatchName.trim();
    if (!trimmed) {
      setError("Batch name cannot be empty.");
      return;
    }
    try {
      await onRenameImportBatch(batchId, trimmed);
      cancelEditingBatch();
    } catch {
      setError("Failed to rename batch. Please try again.");
    }
  };

  const handleEnterRadioClick = () => {
    if (isFullSelected && hasUploadedHistory && !selectedBatch) {
      setShowBatchWarning(true);
      return;
    }
    setShowBatchWarning(false);
    onEnterRadio();
  };

  return (
    <div className="mode-shell mode-shell--heatmap">
      <div className="mode-panel mode-panel--heatmap">
        <div className="mode-header">
          <div className="mode-title">SPOTIFY HUB</div>
        </div>

        <div className="hub-heatmap">
          <section
            className={`hub-tile hub-tile--guide hub-assemble-panel hub-assemble-panel--from-left ${
              isPanelVisible("guide") ? "is-visible" : ""
            }`}
          >
            <div className="mode-subtitle mode-subtitle--compact">
              Start Here
            </div>
            <div className="hub-steps">
              <div className="hub-step">1. Choose a mode</div>
              <div className="hub-step">
                2. Upload data if using Full Listening History
              </div>
              <div className="hub-step">
                3. Launch player or view analytics!
              </div>
            </div>
            {isFullSelected && hasUploadedHistory && !selectedBatch && (
              <div className="hub-warning-pixel">
                SELECT A BATCH FIRST TO ENTER RADIO
              </div>
            )}
          </section>

          <section
            className={`hub-mode-slot hub-mode-slot--quick hub-assemble-panel hub-assemble-panel--from-bottom ${
              isPanelVisible("quick") ? "is-visible" : ""
            }`}
          >
            <button
              type="button"
              className={`mode-btn mode-btn--yearly-hits ${
                isYearlyHitsSelected ? "mode-btn--selected" : ""
              }`}
              disabled={modeLoading}
              onClick={() => onSelectMode("yearly-hits")}
            >
              <span className="mode-btn-title">
                Yearly Hits Playlist (~2020)
              </span>
              <span className="mode-btn-copy">
                Play curated popular songs by release year.
              </span>
            </button>
          </section>

          <section
            className={`hub-mode-slot hub-mode-slot--full hub-assemble-panel hub-assemble-panel--from-right ${
              isPanelVisible("full") ? "is-visible" : ""
            }`}
          >
            <button
              type="button"
              className={`mode-btn mode-btn--timetravel ${
                isFullSelected ? "mode-btn--selected" : ""
              }`}
              disabled={modeLoading}
              onClick={() => onSelectMode("time-travel")}
            >
              <span className="mode-btn-title">Full Listening History</span>
              <span className="mode-btn-copy">
                Upload your extended listening history to get started.
              </span>
            </button>

            <form
              className="upload-form upload-form--inline"
              onSubmit={handleSubmit}
            >
              <div className="upload-card">
                <div className="upload-card-head">
                  <div className="upload-card-title">DROPBOX</div>
                </div>
                <div className="upload-card-copy">
                  Drop in your JSON files here!
                </div>
                <label className="upload-dropzone">
                  <span className="upload-dropzone-label">
                    SELECT FILES (JSON)
                  </span>
                  <input
                    className="upload-file-input"
                    type="file"
                    multiple
                    accept=".json,application/json"
                    onChange={(event) =>
                      setFiles(Array.from(event.target.files || []))
                    }
                  />
                  <span className="upload-file-trigger">CHOOSE FILES</span>
                  <span className="upload-file-hint">JSON files only</span>
                </label>
                <div className="upload-selection">
                  {selectedFileCount > 0
                    ? `${selectedFileCount} file(s) selected`
                    : "No files selected"}
                </div>
                <div className="upload-card-action">
                  <button
                    type="submit"
                    className="mode-btn mode-btn--small mode-btn--timetravel"
                    disabled={isUploading}
                  >
                    {isUploading ? "Processing..." : "Upload & Process"}
                  </button>
                </div>
                {isUploading && (
                  <div className="upload-progress-wrap">
                    <div className="upload-progress-label">{uploadPhase}</div>
                    <div
                      className="upload-progress-track"
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={uploadProgress}
                    >
                      <div
                        className="upload-progress-fill"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <div className="upload-progress-value">
                      {uploadProgress}%
                    </div>
                  </div>
                )}
              </div>
              {error && <div className="upload-error">{error}</div>}
            </form>
          </section>

          <section
            className={`hub-tile hub-tile--batches hub-assemble-panel hub-assemble-panel--from-top ${
              isPanelVisible("batches") ? "is-visible" : ""
            }`}
          >
            <div className="generated-playlists">
              <div className="generated-playlists-head">
                <div className="generated-playlists-title">
                  UPLOADED DATA
                </div>
                <label className="batch-search" aria-label="Search batches">
                  <input
                    type="text"
                    className="batch-search-input"
                    value={batchSearchQuery}
                    onChange={(event) =>
                      setBatchSearchQuery(event.target.value)
                    }
                    placeholder="Search..."
                  />
                </label>
              </div>
              {!playlistsLoading &&
                fullHistoryGroups.length > 0 &&
                filteredHistoryGroups.length > 0 && (
                  <div className="generated-playlists-list">
                    {filteredHistoryGroups.map((group) => (
                      <div
                        key={group.id}
                        className={`generated-playlist-item generated-playlist-item--group ${
                          selectedImportId === group.id
                            ? "generated-playlist-item--selected"
                            : ""
                        }`}
                        onClick={() => {
                          setShowBatchWarning(false);
                          onSelectImportBatch(group.id);
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === "Enter")
                            onSelectImportBatch(group.id);
                        }}
                      >
                        <div className="generated-playlist-toprow">
                          {editingBatchId === group.id ? (
                            <input
                              type="text"
                              className="generated-playlist-name-input"
                              value={editingBatchName}
                              autoFocus
                              maxLength={120}
                              onChange={(event) =>
                                setEditingBatchName(event.target.value)
                              }
                              onClick={(event) => event.stopPropagation()}
                              onBlur={(event) => saveBatchName(event, group.id)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter")
                                  saveBatchName(event, group.id);
                                if (event.key === "Escape")
                                  cancelEditingBatch();
                              }}
                            />
                          ) : (
                            <div
                              className="generated-playlist-main"
                              onClick={(event) =>
                                startEditingBatch(event, group)
                              }
                              title="Click to rename"
                            >
                              {group.custom_name || `Batch #${group.id}`}
                            </div>
                          )}
                          <div className="generated-playlist-actions">
                            <button
                              type="button"
                              className="generated-playlist-analytics"
                              onClick={(event) => {
                                event.stopPropagation();
                                onOpenBatchAnalytics(group.id);
                              }}
                              disabled={
                                Boolean(deletingBatchId) ||
                                editingBatchId === group.id
                              }
                            >
                              VIEW ANALYTICS
                            </button>
                            <button
                              type="button"
                              className="generated-playlist-delete"
                              onClick={(event) =>
                                handleDeleteBatch(event, group.id)
                              }
                              disabled={
                                Boolean(deletingBatchId) ||
                                editingBatchId === group.id
                              }
                            >
                              {deletingBatchId === group.id
                                ? "DELETING"
                                : "DELETE"}
                            </button>
                          </div>
                        </div>
                        <div className="generated-playlist-meta">
                          Created: {formatImportDate(group.created_at)}
                        </div>
                        {deletingBatchId === group.id && (
                          <div className="generated-playlist-progress">
                            <div className="generated-playlist-progress-track">
                              <div
                                className="generated-playlist-progress-fill"
                                style={{ width: `${deleteProgress}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              {!playlistsLoading &&
                fullHistoryGroups.length > 0 &&
                filteredHistoryGroups.length === 0 && (
                  <div className="generated-playlists generated-playlists--empty">
                    No batches match that search.
                  </div>
                )}
              {!playlistsLoading && fullHistoryGroups.length === 0 && (
                <div className="generated-playlists generated-playlists--empty">
                  No full-history batches yet. Upload your Spotify Extended
                  Streaming History JSON to begin.
                </div>
              )}
            </div>
          </section>
        </div>
        <div
          className={`hub-enter-radio-wrap hub-assemble-panel hub-assemble-panel--from-scale ${
            isPanelVisible("enter") ? "is-visible" : ""
          }`}
        >
          <button
            type="button"
            className="mode-btn mode-btn--hero pixel-glow-btn"
            disabled={modeLoading || !canEnterRadio}
            onClick={handleEnterRadioClick}
          >
            <span className="btn-spotify__label">Launch</span>
            <span className="btn-spotify__pixel-burst" aria-hidden="true">
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
            </span>
          </button>
          <div className="hub-summary">
            Mode:{" "}
            {isYearlyHitsSelected
              ? "Yearly Hits"
              : isFullSelected
              ? "Full History"
              : "Not selected"}
            {isFullSelected
              ? ` | Selected: ${
                  selectedBatch
                    ? selectedBatch.custom_name || `Batch #${selectedBatch.id}`
                    : "None"
                }`
              : ""}
          </div>
        </div>
        <ConfirmModal
          isOpen={showDeleteConfirm}
          title="DELETE BATCH?"
          message="This will permanently remove this import batch and all linked analytics snapshots."
          confirmText="DELETE"
          cancelText="KEEP"
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          isDangerous
        />
        <ConfirmModal
          isOpen={showBatchWarning}
          title="BATCH NOT SELECTED"
          message="Select a full-history batch before launching the radio in Full Listening History mode."
          confirmText="OK"
          onConfirm={() => setShowBatchWarning(false)}
          onCancel={() => setShowBatchWarning(false)}
          showCancel={false}
          variant="warning"
        />
        {showScrollPrompt && (
          <div className="hub-scroll-indicator" aria-hidden="true">
            <div className="hub-scroll-indicator-text">SCROLL</div>
            <img
              className="hub-scroll-indicator-arrow"
              src={`${process.env.PUBLIC_URL}/assets/scroll-arrow.png`}
              alt=""
            />
          </div>
        )}
      </div>
    </div>
  );
}

const ONBOARDING_PANELS = [
  {
    id: "request",
    title: "STEP 1 · REQUEST YOUR EXTENDED HISTORY",
    copy: "On Spotify Privacy settings, select only Extended Streaming History, then press Request Data. Do not select Account Data for this import flow.",
    link: "https://www.spotify.com/us/account/privacy/",
    image: `${process.env.PUBLIC_URL}/assets/step1.png`,
    imageAlt:
      "Spotify privacy request screen with Extended Streaming History selected.",
    directionClass: "onboarding-panel--from-left",
  },
  {
    id: "wait",
    title: "STEP 2 · WATCH FOR DELIVERY EMAIL",
    copy: "Spotify sends a download link by email once your archive is ready. Dataset is usually delivered within about 1 to 3 days.",
    image: `${process.env.PUBLIC_URL}/assets/step2.png`,
    imageAlt:
      "Example of Spotify account email delivery notice for requested data.",
    directionClass: "onboarding-panel--from-bottom",
  },
  {
    id: "extract",
    title: "STEP 3 · EXTRACT JSON FILES",
    copy: "Unzip the package and upload ONLY the audio history files to the dropbox.",
    image: `${process.env.PUBLIC_URL}/assets/step3.png`,
    imageAlt:
      "Extracted Spotify data package showing audio streaming history JSON files.",
    directionClass: "onboarding-panel--from-right",
  },
  {
    id: "upload",
    title: "STEP 4 · IMPORT IN FULL HISTORY MODE",
    copy: "Open Full Listening History in Data Hub, choose all JSON files, and run Upload & Process. The app creates one batch with coverage metadata.",
    directionClass: "onboarding-panel--from-top",
    image: `${process.env.PUBLIC_URL}/assets/step4.png`
  },
  {
    id: "select",
    title: "STEP 5 · SELECT A BATCH, ENTER RADIO",
    copy: "Open Analytics to explore your listening patterns, then launch the player and enjoy the songs from the good old days!",
    directionClass: "onboarding-panel--from-scale",
    image: `${process.env.PUBLIC_URL}/assets/step5.png`,
    imageBelow: `${process.env.PUBLIC_URL}/assets/step5-2.png`,
  },
];

const LOCAL_ONBOARDING_PANELS = [
  {
    id: "create",
    title: "STEP 1 · CREATE A PLAYLIST FOLDER",
    copy: "Open Library, enter a playlist name, and create a dedicated folder for a focused collection.",
    directionClass: "onboarding-panel--from-left",
  },
  {
    id: "drop",
    title: "STEP 2 · DROP AUDIO FILES",
    copy: "Drag and drop local audio files into the upload dropbox. Retrofy accepts common formats like mp3, wav, m4a, aac, and flac.",
    directionClass: "onboarding-panel--from-bottom",
  },
  {
    id: "organize",
    title: "STEP 3 · MANAGE YOUR PLAYLISTS",
    copy: "Use the Library playlist section to review folder totals and delete playlists you no longer want available in radio mode.",
    directionClass: "onboarding-panel--from-right",
  },
  {
    id: "browse",
    title: "STEP 4 · BROWSE INSIDE RADIO",
    copy: "Launch radio, pick a folder in the sidebar, and explore tracks with full title hover animations and folder navigation.",
    directionClass: "onboarding-panel--from-top",
  },
  {
    id: "play",
    title: "STEP 5 · PLAY AND CONTROL",
    copy: "Start playback from any local track and use the same radio controls to play, pause, skip, and stop without leaving the player.",
    directionClass: "onboarding-panel--from-scale",
  },
];

function OnboardingWizard({ onOpenWaitlist, onContinueSpotifyLogin }) {
  const [revealedPanelCount, setRevealedPanelCount] = useState(0);
  const wheelDeltaAccumulatorRef = useRef(0);
  const lastRevealTimeRef = useRef(0);
  const panelRefs = useRef([]);
  const totalPanels = ONBOARDING_PANELS.length;
  const revealedSteps = Math.min(revealedPanelCount, totalPanels);
  const isComplete = revealedSteps >= totalPanels;

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) {
      setRevealedPanelCount(totalPanels);
      return undefined;
    }

    const handleWheel = (event) => {
      if (revealedPanelCount >= totalPanels) return;
      if (event.defaultPrevented) return;
      if (event.deltaY <= 0) {
        wheelDeltaAccumulatorRef.current = 0;
        return;
      }

      const now = Date.now();
      if (now - lastRevealTimeRef.current < ONBOARDING_REVEAL_COOLDOWN_MS)
        return;

      wheelDeltaAccumulatorRef.current += event.deltaY;
      if (wheelDeltaAccumulatorRef.current < ONBOARDING_WHEEL_THRESHOLD) return;

      event.preventDefault();
      wheelDeltaAccumulatorRef.current = 0;
      lastRevealTimeRef.current = now;
      const nextCount = Math.min(revealedPanelCount + 1, totalPanels);
      setRevealedPanelCount(nextCount);
      const nextPanel = panelRefs.current[nextCount - 1];
      if (nextPanel) {
        window.requestAnimationFrame(() => {
          nextPanel.scrollIntoView({ behavior: "smooth", block: "center" });
        });
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      window.removeEventListener("wheel", handleWheel);
    };
  }, [revealedPanelCount, totalPanels]);

  return (
    <div className="onboarding-shell">
      <header className="onboarding-header">
        <h1 className="onboarding-title">HOW TO IMPORT YOUR LISTENING HISTORY</h1>
        <div className="onboarding-intro-section">
          <p className="onboarding-intro-text">Unlock your complete Spotify listening history and explore songs from across the years. Follow these 5 simple steps to get started.</p>
          <div className="onboarding-intro-features">
            <div className="onboarding-intro-feature">
              <img src={`${process.env.PUBLIC_URL}/assets/icon-data-8bit.svg`} alt="Data" className="onboarding-intro-feature-icon" />
              <div className="onboarding-intro-feature-label">Access Your Data</div>
            </div>
            <div className="onboarding-intro-feature">
              <img src={`${process.env.PUBLIC_URL}/assets/icon-upload-8bit.svg`} alt="Upload" className="onboarding-intro-feature-icon" />
              <div className="onboarding-intro-feature-label">Upload Files</div>
            </div>
            <div className="onboarding-intro-feature">
              <img src={`${process.env.PUBLIC_URL}/assets/icon-music-8bit.svg`} alt="Music" className="onboarding-intro-feature-icon" />
              <div className="onboarding-intro-feature-label">Explore & Play</div>
            </div>
          </div>
          <div className="onboarding-intro-hint">↓ Scroll down to begin ↓</div>
        </div>
      </header>

      <div className="onboarding-panels">
        {ONBOARDING_PANELS.map((panel, index) => {
          const isVisible = revealedPanelCount > index;
          const panelImages =
            Array.isArray(panel.images) && panel.images.length
              ? panel.images
              : panel.image
              ? [panel.image]
              : [];
          const sideClass =
            index % 2 === 0
              ? "onboarding-panel--left"
              : "onboarding-panel--right";
          return (
            <section
              key={panel.id}
              ref={(element) => {
                panelRefs.current[index] = element;
              }}
              className={`onboarding-panel ${sideClass} hub-assemble-panel ${
                panel.directionClass
              } ${isVisible ? "is-visible" : ""}`}
            >
              <div className="onboarding-panel-step">0{index + 1}</div>
              <h2 className="onboarding-panel-title">
                {panel.link ? (
                  <a
                    className="onboarding-panel-title-link"
                    href={panel.link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {panel.title}
                  </a>
                ) : (
                  panel.title
                )}
              </h2>
              {panelImages.length > 0 ? (
                <div className="onboarding-panel-content onboarding-panel-content--with-media">
                  <div className="onboarding-panel-copy-wrap">
                    <p className="onboarding-panel-copy onboarding-panel-copy--with-media">
                      {panel.copy}
                    </p>
                    {panel.imageBelow && (
                      <img
                        className="onboarding-panel-image onboarding-panel-image--below"
                        src={panel.imageBelow}
                        alt="How-to final step follow-up illustration"
                        loading="lazy"
                      />
                    )}
                  </div>
                  <div className="onboarding-panel-media">
                    {panelImages.map((imageSrc, imageIndex) => (
                      <img
                        key={`${panel.id}-image-${imageIndex}`}
                        className="onboarding-panel-image"
                        src={imageSrc}
                        alt={
                          imageIndex === 0 && panel.imageAlt
                            ? panel.imageAlt
                            : `How-to step illustration ${imageIndex + 1}`
                        }
                        loading="lazy"
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <p className="onboarding-panel-copy">{panel.copy}</p>
              )}
            </section>
          );
        })}
      </div>

      {isComplete && (
        <div className="onboarding-final-cta">
          <button
            type="button"
            className="btn-spotify btn-spotify--final-cta pixel-glow-btn"
            onClick={() => onOpenWaitlist?.()}
          >
            <span className="btn-spotify__label">JOIN WAITLIST</span>
            <span className="btn-spotify__pixel-burst" aria-hidden="true">
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
            </span>
          </button>
          <button
            type="button"
            className="login-approved-link onboarding-approved-link"
            onClick={() => onContinueSpotifyLogin?.()}
          >
            Already approved? Connect Spotify
          </button>
        </div>
      )}

      {!isComplete && (
        <div className="onboarding-scroll-indicator" aria-hidden="true">
          <div className="onboarding-scroll-text">SCROLL FOR NEXT STEP</div>
          <img
            className="onboarding-scroll-arrow"
            src={`${process.env.PUBLIC_URL}/assets/scroll-arrow.png`}
            alt=""
          />
        </div>
      )}
    </div>
  );
}

function LocalOnboardingWizard({ onLaunchRadio }) {
  const [revealedPanelCount, setRevealedPanelCount] = useState(0);
  const wheelDeltaAccumulatorRef = useRef(0);
  const lastRevealTimeRef = useRef(0);
  const panelRefs = useRef([]);
  const totalPanels = LOCAL_ONBOARDING_PANELS.length;
  const revealedSteps = Math.min(revealedPanelCount, totalPanels);
  const isComplete = revealedSteps >= totalPanels;

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) {
      setRevealedPanelCount(totalPanels);
      return undefined;
    }

    const handleWheel = (event) => {
      if (revealedPanelCount >= totalPanels) return;
      if (event.defaultPrevented) return;
      if (event.deltaY <= 0) {
        wheelDeltaAccumulatorRef.current = 0;
        return;
      }

      const now = Date.now();
      if (now - lastRevealTimeRef.current < ONBOARDING_REVEAL_COOLDOWN_MS) {
        return;
      }

      wheelDeltaAccumulatorRef.current += event.deltaY;
      if (wheelDeltaAccumulatorRef.current < ONBOARDING_WHEEL_THRESHOLD) return;

      event.preventDefault();
      wheelDeltaAccumulatorRef.current = 0;
      lastRevealTimeRef.current = now;
      const nextCount = Math.min(revealedPanelCount + 1, totalPanels);
      setRevealedPanelCount(nextCount);
      const nextPanel = panelRefs.current[nextCount - 1];
      if (nextPanel) {
        window.requestAnimationFrame(() => {
          nextPanel.scrollIntoView({ behavior: "smooth", block: "center" });
        });
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      window.removeEventListener("wheel", handleWheel);
    };
  }, [revealedPanelCount, totalPanels]);

  return (
    <div className="onboarding-shell">
      <header className="onboarding-header">
        <h1 className="onboarding-title">HOW TO USE LOCAL MODE</h1>
        <div className="onboarding-intro-section">
          <p className="onboarding-intro-text">
            Build your own local playlists and run the radio without Spotify dependency. Follow these 5 steps to set up your local library cleanly.
          </p>
          <div className="onboarding-intro-features">
            <div className="onboarding-intro-feature">
              <img src={`${process.env.PUBLIC_URL}/assets/icon-upload-8bit.svg`} alt="Upload" className="onboarding-intro-feature-icon" />
              <div className="onboarding-intro-feature-label">Upload Audio</div>
            </div>
            <div className="onboarding-intro-feature">
              <img src={`${process.env.PUBLIC_URL}/assets/icon-data-8bit.svg`} alt="Playlist" className="onboarding-intro-feature-icon" />
              <div className="onboarding-intro-feature-label">Manage Playlists</div>
            </div>
            <div className="onboarding-intro-feature">
              <img src={`${process.env.PUBLIC_URL}/assets/icon-music-8bit.svg`} alt="Play" className="onboarding-intro-feature-icon" />
              <div className="onboarding-intro-feature-label">Launch Radio</div>
            </div>
          </div>
          <div className="onboarding-intro-hint">↓ Scroll down to begin ↓</div>
        </div>
      </header>

      <div className="onboarding-panels">
        {LOCAL_ONBOARDING_PANELS.map((panel, index) => {
          const isVisible = revealedPanelCount > index;
          const sideClass =
            index % 2 === 0
              ? "onboarding-panel--left"
              : "onboarding-panel--right";
          return (
            <section
              key={panel.id}
              ref={(element) => {
                panelRefs.current[index] = element;
              }}
              className={`onboarding-panel ${sideClass} hub-assemble-panel ${
                panel.directionClass
              } ${isVisible ? "is-visible" : ""}`}
            >
              <div className="onboarding-panel-step">0{index + 1}</div>
              <h2 className="onboarding-panel-title">{panel.title}</h2>
              <p className="onboarding-panel-copy">{panel.copy}</p>
            </section>
          );
        })}
      </div>

      {isComplete && (
        <div className="onboarding-final-cta">
          <button
            type="button"
            className="mode-btn mode-btn--hero pixel-glow-btn"
            onClick={() => onLaunchRadio?.()}
          >
            <span className="btn-spotify__label">Launch</span>
            <span className="btn-spotify__pixel-burst" aria-hidden="true">
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
              <span className="btn-spotify__pixel" />
            </span>
          </button>
        </div>
      )}

      {!isComplete && (
        <div className="onboarding-scroll-indicator" aria-hidden="true">
          <div className="onboarding-scroll-text">SCROLL FOR NEXT STEP</div>
          <img
            className="onboarding-scroll-arrow"
            src={`${process.env.PUBLIC_URL}/assets/scroll-arrow.png`}
            alt=""
          />
        </div>
      )}
    </div>
  );
}

function PremiumPlans({ onStartCheckout, onManageBilling, billingStatus, onBack }) {
  const isPremiumUser = Boolean(billingStatus?.is_premium);
  return (
    <div className="mode-shell">
      <div className="mode-panel premium-panel">
        <div className="mode-header">
          <div className="mode-title">PREMIUM PLANS</div>
          <button
            type="button"
            className="mode-btn mode-btn--small analytics-back-btn"
            onClick={onBack}
          >
            Back
          </button>
        </div>
        <div className="mode-subtitle mode-subtitle--compact">
          Unlock full-time travel features and support ongoing development.
        </div>

        <div className="premium-grid">
          <section className="premium-card premium-card--monthly">
            <div className="premium-card-head">
              <div className="premium-card-name">Monthly</div>
              <div className="premium-card-badge">Flexible</div>
            </div>
            <div className="premium-card-price">$4.99</div>
            <div className="premium-card-cycle">per month</div>
            <div className="premium-card-note">All premium features</div>
            <ul className="premium-card-list">
              <li>Unlock all radio themes</li>
              <li>Customize your analytics view</li>
              <li>Deep lifetime analytics, not just highlights</li>
              <li>Insights Spotify Wrapped does not provide</li>
            </ul>
            <button
              type="button"
              className="mode-btn premium-card-cta"
              onClick={() => onStartCheckout?.("monthly")}
              disabled={!onStartCheckout}
            >
              Choose Monthly
            </button>
          </section>

          <section className="premium-card premium-card--yearly">
            <div className="premium-card-head">
              <div className="premium-card-name">Yearly</div>
              <div className="premium-card-badge premium-card-badge--popular">
                Most Popular
              </div>
            </div>
            <div className="premium-card-price">$19.99</div>
            <div className="premium-card-cycle">per year</div>
            <div className="premium-card-note">Same features, billed yearly</div>
            <ul className="premium-card-list">
              <li>Unlock all radio themes</li>
              <li>Customize your analytics view</li>
              <li>Deep lifetime analytics, not just highlights</li>
              <li>Insights Spotify Wrapped does not provide</li>
            </ul>
            <button
              type="button"
              className="mode-btn mode-btn--timetravel premium-card-cta"
              onClick={() => onStartCheckout?.("yearly")}
              disabled={!onStartCheckout}
            >
              Choose Yearly
            </button>
          </section>
        </div>
        {isPremiumUser && (
          <div className="premium-manage-row">
            <button
              type="button"
              className="mode-btn premium-card-cta"
              onClick={onManageBilling}
              disabled={!onManageBilling}
            >
              Manage Billing
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function LegalCenter({ onBack }) {
  return (
    <div className="mode-shell">
      <div className="mode-panel legal-panel">
        <div className="mode-header">
          <div className="mode-title">LEGAL</div>
          <button
            type="button"
            className="mode-btn mode-btn--small analytics-back-btn"
            onClick={onBack}
          >
            Back
          </button>
        </div>

        <section className="legal-block">
          <h3 className="legal-block-title">Privacy Policy</h3>
          <p className="legal-block-copy">
            Retrofy processes Spotify account/profile data and uploaded listening history
            to provide analytics and playback features. Data is used only for product
            functionality, billing operations, support, and service security.
          </p>
          <p className="legal-block-copy">
            Uploaded history is retained to enable your analytics and radio features. You
            can request deletion of your account data by contacting support.
          </p>
        </section>

        <section className="legal-block">
          <h3 className="legal-block-title">Terms of Use</h3>
          <p className="legal-block-copy">
            You are responsible for account activity and use of the service in compliance
            with applicable laws and Spotify platform terms. Abuse, scraping, reverse
            engineering, or misuse of service endpoints is prohibited.
          </p>
        </section>

        <section className="legal-block">
          <h3 className="legal-block-title">Subscription Billing Terms</h3>
          <p className="legal-block-copy">
            Premium plans renew automatically unless canceled before the next billing date.
            Plan pricing, billing cycle, and renewal timing are shown on the Premium page
            before purchase. Cancellation can be managed from billing settings/Stripe
            portal.
          </p>
          <p className="legal-block-copy">
            Refunds are handled case-by-case according to applicable law and payment
            processor policy.
          </p>
        </section>

        <section className="legal-block">
          <h3 className="legal-block-title">Contact</h3>
          <p className="legal-block-copy">
            Support: <a href="mailto:support@retrofy.app">support@retrofy.app</a>
          </p>
        </section>

        <section className="legal-block legal-block--compact">
          <p className="legal-block-copy legal-block-copy--muted">
            © 2026 Retrofy. All rights reserved.
          </p>
        </section>
      </div>
    </div>
  );
}

function RadioTransitionScreen({ onComplete }) {
  const [progressPct, setProgressPct] = useState(0);

  useEffect(() => {
    const startTimer = window.setTimeout(() => {
      setProgressPct(100);
    }, 40);
    const doneTimer = window.setTimeout(() => {
      onComplete?.();
    }, RADIO_TRANSITION_FILL_MS + RADIO_TRANSITION_HOLD_MS);

    return () => {
      window.clearTimeout(startTimer);
      window.clearTimeout(doneTimer);
    };
  }, [onComplete]);

  return (
    <div className="radio-transition-shell" role="status" aria-live="polite">
      <div className="radio-transition-panel">
        <div className="radio-transition-title">TUNING RADIO SIGNAL</div>
        <div className="radio-transition-sub">LOADING YOUR STATION...</div>
        <div className="radio-transition-track">
          <div
            className="radio-transition-fill"
            style={{
              width: `${progressPct}%`,
              transitionDuration: `${RADIO_TRANSITION_FILL_MS}ms`,
            }}
          />
        </div>
      </div>
    </div>
  );
}

// Custom 8-bit themed dropdown component
function RetroDropdown({
  options,
  value,
  onChange,
  label,
  className = "",
  onLockedSelect,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const normalizedOptions = useMemo(
    () =>
      (Array.isArray(options) ? options : []).map((option) => {
        if (option && typeof option === "object" && "value" in option) {
          return {
            value: option.value,
            label: option.label ?? String(option.value),
            locked: Boolean(option.locked),
          };
        }
        return {
          value: option,
          label: String(option),
          locked: false,
        };
      }),
    [options]
  );

  useEffect(() => {
    const handleClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div
      className={`retro-dropdown-wrap ${isOpen ? "is-open" : ""} ${className}`.trim()}
      ref={menuRef}
    >
      <button
        className={`retro-dropdown-btn ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <span className="retro-dropdown-text">{String(value)}</span>
        <span className="retro-dropdown-arrow" aria-hidden="true">▼</span>
      </button>
      {isOpen && (
        <div className="retro-dropdown-menu">
          {normalizedOptions.map((option) => (
            <button
              key={`${String(option.value)}:${option.label}`}
              className={`retro-dropdown-item ${option.value === value ? 'active' : ''} ${
                option.locked ? 'retro-dropdown-item--locked' : ''
              }`}
              onClick={() => {
                if (option.locked) {
                  onLockedSelect?.(option.value);
                  setIsOpen(false);
                  return;
                }
                onChange(option.value);
                setIsOpen(false);
              }}
              type="button"
            >
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BatchAnalytics({
  importBatches,
  analyticsBatchId,
  onSelectBatch,
  onBackToHub,
  onEnterRadioFromAnalytics,
  billingStatus,
  onOpenPremium,
}) {
  const ENTRY_OPTIONS = [5, 10, 30, 50];
  const CONCENTRATION_OPTIONS = [10, 50, 100];
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [analyticsByYear, setAnalyticsByYear] = useState([]);
  const [monthlyActivityByYear, setMonthlyActivityByYear] = useState({});
  const [yearOptions, setYearOptions] = useState([]);
  const [chartYearRanges, setChartYearRanges] = useState({
    topArtists: { from: null, to: null },
    topTracks: { from: null, to: null },
    topAlbums: { from: null, to: null },
    yearlyStreaming: { from: null, to: null },
    monthlyActivity: { from: null, to: null },
  });
  const [advancedAnalytics, setAdvancedAnalytics] = useState(null);
  const [artistConcentrationTopN, setArtistConcentrationTopN] = useState(10);
  const [trackConcentrationTopN, setTrackConcentrationTopN] = useState(10);
  const [premiumFeaturePrompt, setPremiumFeaturePrompt] = useState("");
  const [habitSelections, setHabitSelections] = useState({
    device: "",
    connectivity: "",
    shuffle: "",
    skip: "",
  });
  const [visibleSectionCount, setVisibleSectionCount] = useState(0);
  const wheelDeltaAccumulatorRef = useRef(0);
  const lastRevealTimeRef = useRef(0);
  const [chartEntryLimits, setChartEntryLimits] = useState({
    topArtists: 10,
    topTracks: 10,
    topAlbums: 10,
    yearlyStreaming: 10,
    nicheHighlights: 10,
    seasonalTasteMap: 10,
  });

  const selectedBatch =
    importBatches.find((batch) => batch.id === analyticsBatchId) ||
    importBatches[0] ||
    null;
  const isPremiumUser = Boolean(billingStatus?.is_premium);
  const lockIconSrc = `${process.env.PUBLIC_URL}/assets/icon-lock-8bit.svg`;

  useEffect(() => {
    if (!selectedBatch?.id) {
      setAnalyticsByYear([]);
      setMonthlyActivityByYear({});
      setYearOptions([]);
      setChartYearRanges({
        topArtists: { from: null, to: null },
        topTracks: { from: null, to: null },
        topAlbums: { from: null, to: null },
        yearlyStreaming: { from: null, to: null },
        monthlyActivity: { from: null, to: null },
      });
      setArtistConcentrationTopN(10);
      setTrackConcentrationTopN(10);
      setPremiumFeaturePrompt("");
      setHabitSelections({
        device: "",
        connectivity: "",
        shuffle: "",
        skip: "",
      });
      setAdvancedAnalytics(null);
      setVisibleSectionCount(0);
      return;
    }

    const run = async () => {
      setLoading(true);
      setError("");
      setVisibleSectionCount(0);
      try {
        await fetch(
          `${API_ORIGIN}/api/history/imports/${selectedBatch.id}/select`,
          {
            method: "POST",
            credentials: "include",
          }
        );

        const [yearsResp, advancedResp] = await Promise.all([
          fetch(`${API_ORIGIN}/api/years`, { credentials: "include" }),
          fetch(
            `${API_ORIGIN}/api/history/imports/${selectedBatch.id}/analytics`,
            { credentials: "include" }
          ),
        ]);
        if (!yearsResp.ok) throw new Error("Failed to fetch years");
        const yearsData = await yearsResp.json();
        const advancedData = advancedResp.ok ? await advancedResp.json() : null;
        const years = (Array.isArray(yearsData?.years) ? yearsData.years : [])
          .filter((year) => Number.isInteger(year))
          .sort((a, b) => a - b);

        const byYear = [];

        for (const year of years) {
          const [artistsResp, tracksResp] = await Promise.all([
            fetch(`${API_ORIGIN}/api/year/${year}/top-artists?limit=100`, {
              credentials: "include",
            }),
            fetch(`${API_ORIGIN}/api/year/${year}/top-tracks?limit=500`, {
              credentials: "include",
            }),
          ]);
          if (!artistsResp.ok || !tracksResp.ok) {
            throw new Error(`Failed to fetch yearly analytics for ${year}`);
          }

          const artistsData = await artistsResp.json();
          const tracksData = await tracksResp.json();
          const artists = Array.isArray(artistsData?.artists)
            ? artistsData.artists
            : [];
          const tracks = Array.isArray(tracksData?.tracks)
            ? tracksData.tracks
            : [];

          const yearlyArtists = artists.map((artist) => ({
            label: artist?.artist_name || "Unknown Artist",
            value: Number(artist?.play_count || 0),
          }));
          const yearlyTracks = tracks.map((track) => ({
            key:
              track?.spotify_track_id ||
              `${track?.track_name || "Unknown"}::${track?.artist_name || "Unknown"}`,
            label: `${track?.track_name || "Unknown"} - ${
              track?.artist_name || "Unknown Artist"
            }`,
            value: Number(track?.play_count || 0),
          }));
          const yearlyAlbumMap = new Map();

          let totalMs = 0;
          let totalPlays = 0;
          tracks.forEach((track) => {
            const plays = Number(track?.play_count || 0);
            const albumName = track?.album_name || "Unknown Album";
            const albumArtist = track?.artist_name || "Unknown Artist";
            const albumKey = `${albumName}::${albumArtist}`;
            const albumLabel = `${albumName} - ${albumArtist}`;
            const existing = yearlyAlbumMap.get(albumKey) || { label: albumLabel, value: 0 };
            existing.value += plays;
            yearlyAlbumMap.set(albumKey, existing);
            totalPlays += plays;
            totalMs += Number(track?.total_ms_played || 0);
          });
          const yearlyAlbums = Array.from(yearlyAlbumMap.values());

          byYear.push({
            year,
            artists: yearlyArtists,
            tracks: yearlyTracks,
            albums: yearlyAlbums,
            totalMs,
            totalPlays,
          });
        }

        setAnalyticsByYear(byYear);
        setMonthlyActivityByYear({});
        setYearOptions(years);
        const defaultFrom = years[0] ?? null;
        const defaultTo = years[years.length - 1] ?? null;
        setChartYearRanges({
          topArtists: { from: defaultFrom, to: defaultTo },
          topTracks: { from: defaultFrom, to: defaultTo },
          topAlbums: { from: defaultFrom, to: defaultTo },
          yearlyStreaming: { from: defaultFrom, to: defaultTo },
          monthlyActivity: { from: defaultFrom, to: defaultTo },
        });
        setArtistConcentrationTopN(10);
        setTrackConcentrationTopN(10);
        setPremiumFeaturePrompt("");
        setHabitSelections({
          device: "",
          connectivity: "",
          shuffle: "",
          skip: "",
        });
        setAdvancedAnalytics(advancedData?.analytics || null);
      } catch (fetchError) {
        setError("Could not load analytics for this batch yet.");
        setAnalyticsByYear([]);
        setMonthlyActivityByYear({});
        setYearOptions([]);
        setChartYearRanges({
          topArtists: { from: null, to: null },
          topTracks: { from: null, to: null },
          topAlbums: { from: null, to: null },
          yearlyStreaming: { from: null, to: null },
          monthlyActivity: { from: null, to: null },
        });
        setArtistConcentrationTopN(10);
        setTrackConcentrationTopN(10);
        setPremiumFeaturePrompt("");
        setHabitSelections({
          device: "",
          connectivity: "",
          shuffle: "",
          skip: "",
        });
        setAdvancedAnalytics(null);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [selectedBatch?.id]);

  useEffect(() => {
    if (loading || error || !selectedBatch?.id) {
      setVisibleSectionCount(0);
      return;
    }

    const totalSections = advancedAnalytics ? 8 : 3;
    setVisibleSectionCount((prev) => {
      if (prev >= 1) return Math.min(prev, totalSections);
      return 1;
    });
  }, [loading, error, selectedBatch?.id, advancedAnalytics]);

  useEffect(() => {
    if (loading || error || !selectedBatch?.id) return undefined;

    const totalSections = advancedAnalytics ? 8 : 3;
    if (visibleSectionCount >= totalSections) return undefined;

    const handleWheel = (event) => {
      if (event.defaultPrevented) return;
      if (event.deltaY <= 0) {
        wheelDeltaAccumulatorRef.current = 0;
        return;
      }

      const now = Date.now();
      if (now - lastRevealTimeRef.current < ANALYTICS_REVEAL_COOLDOWN_MS) {
        return;
      }

      wheelDeltaAccumulatorRef.current += event.deltaY;
      if (wheelDeltaAccumulatorRef.current < ANALYTICS_WHEEL_THRESHOLD) return;

      event.preventDefault();
      wheelDeltaAccumulatorRef.current = 0;
      lastRevealTimeRef.current = now;
      setVisibleSectionCount((prev) => Math.min(prev + 1, totalSections));
    };

    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      window.removeEventListener("wheel", handleWheel);
    };
  }, [loading, error, selectedBatch?.id, advancedAnalytics, visibleSectionCount]);

  const updateChartEntryLimit = (chartKey, nextLimit) => {
    const cappedLimit = isPremiumUser
      ? nextLimit
      : Math.min(Number(nextLimit || 10), 10);
    setChartEntryLimits((prev) => ({
      ...prev,
      [chartKey]: cappedLimit,
    }));
  };

  const effectiveChartYearRanges = useMemo(() => {
    const fallbackFrom = yearOptions[0] ?? null;
    const fallbackTo = yearOptions[yearOptions.length - 1] ?? null;
    const normalizeRange = (range) => {
      const from = Number.isInteger(range?.from) ? range.from : fallbackFrom;
      const to = Number.isInteger(range?.to) ? range.to : fallbackTo;
      if (!Number.isInteger(from) || !Number.isInteger(to)) {
        return { from: null, to: null };
      }
      return from <= to ? { from, to } : { from: to, to: from };
    };
    return {
      topArtists: normalizeRange(chartYearRanges.topArtists),
      topTracks: normalizeRange(chartYearRanges.topTracks),
      topAlbums: normalizeRange(chartYearRanges.topAlbums),
      yearlyStreaming: normalizeRange(chartYearRanges.yearlyStreaming),
      monthlyActivity: normalizeRange(chartYearRanges.monthlyActivity),
    };
  }, [chartYearRanges, yearOptions]);

  const filteredYearBucketsByChart = useMemo(() => {
    const filterByRange = (range) => {
      if (!analyticsByYear.length) return [];
      const start = range?.from ?? -Infinity;
      const end = range?.to ?? Infinity;
      return analyticsByYear.filter((entry) => entry.year >= start && entry.year <= end);
    };
    return {
      topArtists: filterByRange(effectiveChartYearRanges.topArtists),
      topTracks: filterByRange(effectiveChartYearRanges.topTracks),
      topAlbums: filterByRange(effectiveChartYearRanges.topAlbums),
      yearlyStreaming: filterByRange(effectiveChartYearRanges.yearlyStreaming),
      monthlyActivity: filterByRange(effectiveChartYearRanges.monthlyActivity),
    };
  }, [analyticsByYear, effectiveChartYearRanges]);

  const topArtists = useMemo(() => {
    const artistMap = new Map();
    filteredYearBucketsByChart.topArtists.forEach((bucket) => {
      bucket.artists.forEach((artist) => {
        const key = artist?.label || "Unknown Artist";
        const prev = artistMap.get(key) || { label: key, value: 0 };
        prev.value += Number(artist?.value || 0);
        artistMap.set(key, prev);
      });
    });
    return Array.from(artistMap.values()).sort((a, b) => b.value - a.value);
  }, [filteredYearBucketsByChart.topArtists]);

  const topTracks = useMemo(() => {
    const trackMap = new Map();
    filteredYearBucketsByChart.topTracks.forEach((bucket) => {
      bucket.tracks.forEach((track) => {
        const key = track?.key || track?.label || "Unknown Track";
        const prev = trackMap.get(key) || { label: track.label, value: 0 };
        prev.value += Number(track?.value || 0);
        trackMap.set(key, prev);
      });
    });
    return Array.from(trackMap.values()).sort((a, b) => b.value - a.value);
  }, [filteredYearBucketsByChart.topTracks]);

  const topAlbums = useMemo(() => {
    const albumMap = new Map();
    filteredYearBucketsByChart.topAlbums.forEach((bucket) => {
      (bucket.albums || []).forEach((album) => {
        const key = album?.label || "Unknown Album";
        const prev = albumMap.get(key) || { label: key, value: 0 };
        prev.value += Number(album?.value || 0);
        albumMap.set(key, prev);
      });
    });
    return Array.from(albumMap.values()).sort((a, b) => b.value - a.value);
  }, [filteredYearBucketsByChart.topAlbums]);

  const yearlyStreaming = useMemo(
    () =>
      filteredYearBucketsByChart.yearlyStreaming
        .map((bucket) => ({
          label: String(bucket.year),
          value: Number(bucket.totalMs || 0),
          plays: Number(bucket.totalPlays || 0),
        }))
        .sort((a, b) => Number(a.label) - Number(b.label)),
    [filteredYearBucketsByChart.yearlyStreaming]
  );

  const monthlyActivityYearsInRange = useMemo(
    () =>
      filteredYearBucketsByChart.monthlyActivity
        .map((bucket) => Number(bucket.year))
        .filter((year) => Number.isInteger(year)),
    [filteredYearBucketsByChart.monthlyActivity]
  );

  useEffect(() => {
    if (!selectedBatch?.id || monthlyActivityYearsInRange.length === 0) return;
    const missingYears = monthlyActivityYearsInRange.filter(
      (year) => !Object.prototype.hasOwnProperty.call(monthlyActivityByYear, year)
    );
    if (missingYears.length === 0) return;

    let cancelled = false;

    const fetchMissingYears = async () => {
      const results = await Promise.all(
        missingYears.map(async (year) => {
          try {
            const response = await fetch(
              `${API_ORIGIN}/api/year/${year}/monthly-activity`,
              { credentials: "include" }
            );
            if (!response.ok) throw new Error("Failed to fetch monthly activity");
            const data = await response.json();
            const monthly = Array.isArray(data?.monthly_activity)
              ? data.monthly_activity.map((item) => ({
                  label: item?.label || "",
                  value: Number(item?.value || 0),
                }))
              : [];
            return [year, monthly];
          } catch (_error) {
            return [year, []];
          }
        })
      );
      if (cancelled) return;
      setMonthlyActivityByYear((prev) => {
        const next = { ...prev };
        results.forEach(([year, rows]) => {
          next[year] = rows;
        });
        return next;
      });
    };

    fetchMissingYears();
    return () => {
      cancelled = true;
    };
  }, [selectedBatch?.id, monthlyActivityYearsInRange, monthlyActivityByYear]);

  const monthlyActivityData = useMemo(() => {
    const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthTotals = new Map(monthLabels.map((label) => [label, 0]));
    monthlyActivityYearsInRange.forEach((year) => {
      const rows = monthlyActivityByYear[year];
      if (!Array.isArray(rows)) return;
      rows.forEach((item) => {
        const label = item?.label;
        if (!monthTotals.has(label)) return;
        monthTotals.set(label, monthTotals.get(label) + Number(item?.value || 0));
      });
    });
    return monthLabels.map((label) => ({
      label,
      value: monthTotals.get(label) || 0,
    }));
  }, [monthlyActivityByYear, monthlyActivityYearsInRange]);

  const renderBars = (
    title,
    data,
    chartKey,
    formatter = (value) => value.toLocaleString(),
    { showLimitControl = true, titleAddon = null } = {}
  ) => {
    const selectedLimitRaw = chartEntryLimits[chartKey] || 10;
    const selectedLimit = isPremiumUser
      ? selectedLimitRaw
      : Math.min(selectedLimitRaw, 10);
    const availableEntryOptions = isPremiumUser
      ? ENTRY_OPTIONS
      : ENTRY_OPTIONS.map((option) =>
          option <= 10
            ? option
            : { value: option, label: `${option}`, locked: true }
        );
    const displayedData = showLimitControl
      ? data.slice(0, selectedLimit)
      : isPremiumUser
        ? data
        : data.slice(0, 10);
    const max = displayedData.reduce(
      (acc, item) => Math.max(acc, Number(item.value || 0)),
      0
    );
    return (
      <div className="analytics-chart">
        <div className="analytics-chart-head">
          <div className="analytics-chart-title">
            {title}
            {titleAddon ? (
              <span className="analytics-chart-title-addon">{titleAddon}</span>
            ) : null}
          </div>
          {showLimitControl ? (
            <label className="analytics-row-limit">
              <span className="analytics-row-limit-label">Entries</span>
              <span className="analytics-row-limit-control">
                <RetroDropdown
                  options={availableEntryOptions}
                  value={selectedLimit}
                  onChange={(count) =>
                    updateChartEntryLimit(chartKey, Number(count || 10))
                  }
                  onLockedSelect={() =>
                    setPremiumFeaturePrompt(
                      "Viewing more than 10 entries in charts is available for Premium users."
                    )
                  }
                />
              </span>
            </label>
          ) : null}
        </div>
        <div className="analytics-chart-rows">
          {displayedData.length === 0 && (
            <div className="analytics-chart-empty">No data</div>
          )}
          {displayedData.map((item) => {
            const width = max > 0 ? (Number(item.value || 0) / max) * 100 : 0;
            return (
              <div key={`${chartKey}:${item.label}`} className="analytics-chart-row">
                <div className="analytics-chart-label" title={item.label}>
                  {item.label}
                </div>
                <div className="analytics-chart-bar-track">
                  <div
                    className="analytics-chart-bar-fill"
                    style={{
                      "--target-width": `${Math.max(width, 2)}%`,
                    }}
                  />
                </div>
                <div className="analytics-chart-value">
                  {formatter(Number(item.value || 0))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderKeyValueRows = (
    title,
    rows,
    chartKey,
    { showLimitControl = true, titleAddon = null } = {}
  ) => {
    const selectedLimitRaw = chartEntryLimits[chartKey] || 10;
    const selectedLimit = isPremiumUser
      ? selectedLimitRaw
      : Math.min(selectedLimitRaw, 10);
    const availableEntryOptions = isPremiumUser
      ? ENTRY_OPTIONS
      : ENTRY_OPTIONS.map((option) =>
          option <= 10
            ? option
            : { value: option, label: `${option}`, locked: true }
        );
    const displayedRows = showLimitControl
      ? rows.slice(0, selectedLimit)
      : isPremiumUser
        ? rows
        : rows.slice(0, 10);
    return (
      <div className="analytics-chart">
        <div className="analytics-chart-head">
          <div className="analytics-chart-title">
            {title}
            {titleAddon ? (
              <span className="analytics-chart-title-addon">{titleAddon}</span>
            ) : null}
          </div>
          {showLimitControl ? (
            <label className="analytics-row-limit">
              <span className="analytics-row-limit-label">Entries</span>
              <span className="analytics-row-limit-control">
                <RetroDropdown
                  options={availableEntryOptions}
                  value={selectedLimit}
                  onChange={(count) =>
                    updateChartEntryLimit(chartKey, Number(count || 10))
                  }
                  onLockedSelect={() =>
                    setPremiumFeaturePrompt(
                      "Viewing more than 10 entries in charts is available for Premium users."
                    )
                  }
                />
              </span>
            </label>
          ) : null}
        </div>
        <div className="analytics-key-value-rows">
          {displayedRows.length === 0 && (
            <div className="analytics-chart-empty">No data</div>
          )}
          {displayedRows.map((row) => (
            <div key={row.key || row.label} className="analytics-key-value-row">
              <span>{row.labelNode || row.label}</span>
              <strong>{row.value}</strong>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const playbackHabitOptions = useMemo(() => {
    const habits = advancedAnalytics?.charts?.playback_habits || {};
    const normalizeDeviceGroup = (rawLabel) => {
      const raw = String(rawLabel || "").trim();
      if (!raw) return "Unknown device";
      const platform = raw.toLowerCase();
      if (platform.includes("web_player") || platform.includes("web player"))
        return "Web Player";
      if (platform.includes("ios") || platform.includes("iphone") || platform.includes("ipad"))
        return "iOS";
      if (platform.includes("android")) return "Android";
      if (
        platform.includes("os x") ||
        platform.includes("osx") ||
        platform.includes("macos") ||
        platform.includes("mac")
      )
        return "macOS";
      if (platform.includes("windows")) return "Windows";
      if (platform.includes("linux")) return "Linux";
      if (platform === "not_applicable" || platform === "unknown")
        return "Unknown device";
      return "Other";
    };
    const normalize = (rows) =>
      (Array.isArray(rows) ? rows : [])
        .map((row) => ({
          label: String(row?.label || "").trim(),
          value: Number(row?.value || 0),
        }))
        .filter((row) => row.label.length > 0 && row.value > 0)
        .sort((a, b) => b.value - a.value);
    const normalizeDeviceRows = (rows) => {
      const bucket = new Map();
      normalize(rows).forEach((row) => {
        const label = normalizeDeviceGroup(row.label);
        bucket.set(label, (bucket.get(label) || 0) + Number(row.value || 0));
      });
      return Array.from(bucket.entries())
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => b.value - a.value);
    };
    return {
      devices: normalizeDeviceRows(habits.devices),
      connectivity: normalize(habits.connectivity),
      shuffle: normalize(habits.shuffle_mode),
      skip: normalize(habits.skip_status),
    };
  }, [advancedAnalytics]);

  useEffect(() => {
    const next = {
      device:
        playbackHabitOptions.devices[0]?.label || "",
      connectivity:
        playbackHabitOptions.connectivity[0]?.label || "",
      shuffle:
        playbackHabitOptions.shuffle[0]?.label || "",
      skip:
        playbackHabitOptions.skip[0]?.label || "",
    };
    setHabitSelections((prev) => ({
      device:
        playbackHabitOptions.devices.some((opt) => opt.label === prev.device)
          ? prev.device
          : next.device,
      connectivity:
        playbackHabitOptions.connectivity.some(
          (opt) => opt.label === prev.connectivity
        )
          ? prev.connectivity
          : next.connectivity,
      shuffle:
        playbackHabitOptions.shuffle.some((opt) => opt.label === prev.shuffle)
          ? prev.shuffle
          : next.shuffle,
      skip:
        playbackHabitOptions.skip.some((opt) => opt.label === prev.skip)
          ? prev.skip
          : next.skip,
    }));
  }, [playbackHabitOptions]);

  const renderHabitBars = (title) => {
    const selectedByCategory = {
      device:
        playbackHabitOptions.devices.find(
          (opt) => opt.label === habitSelections.device
        ) || playbackHabitOptions.devices[0] || null,
      connectivity:
        playbackHabitOptions.connectivity.find(
          (opt) => opt.label === habitSelections.connectivity
        ) || playbackHabitOptions.connectivity[0] || null,
      shuffle:
        playbackHabitOptions.shuffle.find(
          (opt) => opt.label === habitSelections.shuffle
        ) || playbackHabitOptions.shuffle[0] || null,
      skip:
        playbackHabitOptions.skip.find((opt) => opt.label === habitSelections.skip) ||
        playbackHabitOptions.skip[0] ||
        null,
    };

    const rows = [
      {
        key: "device",
        option: selectedByCategory.device,
        options: playbackHabitOptions.devices,
        prefix: "of your streams were on",
        suffix: ".",
      },
      {
        key: "connectivity",
        option: selectedByCategory.connectivity,
        options: playbackHabitOptions.connectivity,
        prefix: "of your streams were in",
        suffix: "mode.",
      },
      {
        key: "shuffle",
        option: selectedByCategory.shuffle,
        options: playbackHabitOptions.shuffle,
        prefix: "of your streams were played with",
        suffix: ".",
      },
      {
        key: "skip",
        option: selectedByCategory.skip,
        options: playbackHabitOptions.skip,
        prefix: "of your streams were",
        suffix: ".",
      },
    ].filter((row) => row.option && row.options.length > 0);

    return (
      <div className="analytics-chart analytics-chart--habits">
        <div className="analytics-chart-head">
          <div className="analytics-chart-title">{title}</div>
        </div>
        <div className="analytics-habit-rows">
          {rows.length === 0 && (
            <div className="analytics-chart-empty">No data</div>
          )}
          {rows.map((row) => {
            const value = Math.max(
              0,
              Math.min(100, Number(row?.option?.value || 0))
            );
            return (
              <div key={`habit:${row.key}`} className="analytics-habit-row">
                <div className="analytics-habit-text">
                  <span>{`${value.toFixed(1)}% ${row.prefix} `}</span>
                  <RetroDropdown
                    options={row.options.map((opt) => opt.label)}
                    value={row.option.label}
                    onChange={(label) =>
                      setHabitSelections((prev) => ({
                        ...prev,
                        [row.key]: String(label),
                      }))
                    }
                    className="retro-dropdown-wrap--habit"
                  />
                  <span>{` ${row.suffix}`}</span>
                </div>
                <div className="analytics-habit-track">
                  <div
                    className="analytics-habit-fill"
                    style={{ "--target-width": `${Math.max(value, 1)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPremiumLockedSection = (title) => (
    <div className="analytics-locked-card" aria-label={`${title} premium lock`}>
      <img className="analytics-locked-icon" src={lockIconSrc} alt="" aria-hidden="true" />
      <div className="analytics-locked-title">{title}</div>
      <div className="analytics-locked-copy">Premium required</div>
    </div>
  );

  const concentrationValues = useMemo(() => {
    const artistMap = new Map();
    const trackMap = new Map();
    analyticsByYear.forEach((bucket) => {
      bucket.artists.forEach((artist) => {
        const key = artist?.label || "Unknown Artist";
        artistMap.set(key, (artistMap.get(key) || 0) + Number(artist?.value || 0));
      });
      bucket.tracks.forEach((track) => {
        const key = track?.key || track?.label || "Unknown Track";
        trackMap.set(key, (trackMap.get(key) || 0) + Number(track?.value || 0));
      });
    });
    return {
      artistValues: Array.from(artistMap.values()).sort((a, b) => b - a),
      trackValues: Array.from(trackMap.values()).sort((a, b) => b - a),
    };
  }, [analyticsByYear]);

  const getConcentrationPct = useCallback((values, topN) => {
    if (!Array.isArray(values) || values.length === 0) return 0;
    const total = values.reduce((acc, v) => acc + Number(v || 0), 0);
    if (total <= 0) return 0;
    const topSum = values.slice(0, Math.max(1, Number(topN) || 10)).reduce((acc, v) => acc + Number(v || 0), 0);
    return (topSum / total) * 100;
  }, []);

  const renderConcentrationLabel = (kind, topN, onChange) => (
    <span className="analytics-concentration-label-wrap">
      <span>{`${kind} concentration`}</span>
      <span className="analytics-year-range-control-wrap analytics-year-range-control-wrap--compact">
        <span className="analytics-year-range-text">top</span>
        <RetroDropdown
          options={CONCENTRATION_OPTIONS}
          value={topN}
          onChange={(value) => onChange(Number(value) || 10)}
          className="retro-dropdown-wrap--year"
        />
      </span>
    </span>
  );

  const sideStatsRows = [
    {
      label: "Longest streak (days)",
      value: String(advancedAnalytics?.highlights?.longest_streak_days || 0),
    },
    {
      label: "Best day by minutes",
      value: advancedAnalytics?.highlights?.best_day_of_week
        ? `${advancedAnalytics.highlights.best_day_of_week.label} (${Math.round(
            advancedAnalytics.highlights.best_day_of_week.value
          )} min)`
        : "N/A",
    },
    {
      label: "Best hour by minutes",
      value: advancedAnalytics?.highlights?.best_hour_of_day
        ? `${advancedAnalytics.highlights.best_hour_of_day.label} (${Math.round(
            advancedAnalytics.highlights.best_hour_of_day.value
          )} min)`
        : "N/A",
    },
    {
      label: "Longest single session",
      value: `${Number(
        advancedAnalytics?.highlights?.longest_session_minutes || 0
      ).toLocaleString()} min`,
    },
    {
      key: "artist-concentration",
      label: "Artist concentration",
      labelNode: renderConcentrationLabel(
        "Artist",
        artistConcentrationTopN,
        setArtistConcentrationTopN
      ),
      value: formatPercent(
        getConcentrationPct(
          concentrationValues.artistValues,
          artistConcentrationTopN
        )
      ),
    },
    {
      key: "track-concentration",
      label: "Track concentration",
      labelNode: renderConcentrationLabel(
        "Track",
        trackConcentrationTopN,
        setTrackConcentrationTopN
      ),
      value: formatPercent(
        getConcentrationPct(
          concentrationValues.trackValues,
          trackConcentrationTopN
        )
      ),
    },
  ];

  const handleChartYearFromChange = (chartKey, nextYear) => {
    const next = Number(nextYear);
    if (!Number.isInteger(next)) return;
    setChartYearRanges((prev) => {
      const existing = prev[chartKey] || { from: null, to: null };
      const currentTo = Number.isInteger(existing.to) ? existing.to : next;
      return {
        ...prev,
        [chartKey]: {
          from: next,
          to: next > currentTo ? next : currentTo,
        },
      };
    });
  };

  const handleChartYearToChange = (chartKey, nextYear) => {
    const next = Number(nextYear);
    if (!Number.isInteger(next)) return;
    setChartYearRanges((prev) => {
      const existing = prev[chartKey] || { from: null, to: null };
      const currentFrom = Number.isInteger(existing.from) ? existing.from : next;
      return {
        ...prev,
        [chartKey]: {
          from: next < currentFrom ? next : currentFrom,
          to: next,
        },
      };
    });
  };

  const renderYearRangeControl = (chartKey) => {
    if (!yearOptions.length) return null;
    const range = effectiveChartYearRanges[chartKey];
    return (
      <span className="analytics-year-range-control-wrap">
        <span className="analytics-year-range-text">from</span>
        <RetroDropdown
          options={yearOptions}
          value={range?.from ?? yearOptions[0]}
          onChange={(value) => handleChartYearFromChange(chartKey, value)}
          className="retro-dropdown-wrap--year"
        />
        <span className="analytics-year-range-text">to</span>
        <RetroDropdown
          options={yearOptions}
          value={range?.to ?? yearOptions[yearOptions.length - 1]}
          onChange={(value) => handleChartYearToChange(chartKey, value)}
          className="retro-dropdown-wrap--year"
        />
      </span>
    );
  };

  const analyticsSections = [
    {
      key: "top-artists",
      title: "Most Played Artists",
      content: renderBars("Most Played Artists", topArtists, "topArtists", undefined, {
        titleAddon: renderYearRangeControl("topArtists"),
      }),
    },
    {
      key: "top-tracks",
      title: "Most Played Soundtracks",
      content: renderBars("Most Played Soundtracks", topTracks, "topTracks", undefined, {
        titleAddon: renderYearRangeControl("topTracks"),
      }),
    },
    {
      key: "yearly-streaming",
      title: "Yearly Streaming Time",
      content: renderBars(
        "Yearly Streaming Time",
        yearlyStreaming,
        "yearlyStreaming",
        formatMinutes,
        { showLimitControl: false, titleAddon: renderYearRangeControl("yearlyStreaming") }
      ),
    },
  ];

  if (advancedAnalytics) {
    analyticsSections.push({
      key: "top-albums",
      title: "Most Played Albums",
      premiumOnly: true,
      content: renderBars("Most Played Albums", topAlbums, "topAlbums", undefined, {
        titleAddon: renderYearRangeControl("topAlbums"),
      }),
    });
    analyticsSections.push({
      key: "monthly-activity",
      title: "Listening Activity by Month",
      premiumOnly: true,
      content: renderBars(
        "Listening Activity by Month",
        monthlyActivityData,
        "monthlyActivity",
        formatMinutes,
        { showLimitControl: false, titleAddon: renderYearRangeControl("monthlyActivity") }
      ),
    });
    analyticsSections.push({
      key: "playback-habits",
      title: "Playback Habits",
      premiumOnly: true,
      content: renderHabitBars("Playback Habits"),
    });
    analyticsSections.push({
      key: "side-stats",
      title: "Side Stats",
      premiumOnly: true,
      content: renderKeyValueRows("Side Stats", sideStatsRows, "nicheHighlights", {
        showLimitControl: false,
      }),
    });
    analyticsSections.push({
      key: "seasonal-taste-map",
      title: "Seasonal Taste Map",
      premiumOnly: true,
      content: renderBars(
        "Seasonal Taste Map",
        Array.isArray(advancedAnalytics?.charts?.seasonal_taste_map)
          ? advancedAnalytics.charts.seasonal_taste_map.map((item) => ({
              ...item,
              value: Number(item?.value || 0) * 60 * 1000,
            }))
          : [],
        "seasonalTasteMap",
        formatMinutes,
        { showLimitControl: false }
      ),
    });
  }

  const showAnalyticsScrollPrompt =
    !loading &&
    !error &&
    visibleSectionCount > 0 &&
    visibleSectionCount < analyticsSections.length;

  const visibleAnalyticsSections = analyticsSections
    .slice(0, visibleSectionCount)
    .map((section) => ({
      ...section,
      content:
        !isPremiumUser && section.premiumOnly
          ? renderPremiumLockedSection(section.title || "Premium section")
          : section.content,
    }));

  return (
    <>
      <div className="mode-shell">
        <div className="mode-panel analytics-panel">
        <div className="mode-header">
          <div className="mode-title">LISTENING HISTORY ANALYTICS</div>
          <button
            type="button"
            className="mode-btn mode-btn--small analytics-back-btn"
            onClick={onBackToHub}
          >
            Back
          </button>
        </div>
        {selectedBatch ? (
          <>
            <div className="analytics-batch-meta">
              <div className="analytics-batch-name">
                {selectedBatch.custom_name || `Batch #${selectedBatch.id}`}
              </div>
              <div className="analytics-batch-sub">
                Created: {formatImportDate(selectedBatch.created_at)} | Date
                Range:{" "}
                {formatRange(
                  selectedBatch.earliest_play,
                  selectedBatch.latest_play
                )}
              </div>
            </div>
            <div className="analytics-cards">
              <div className="analytics-card">
                <span>Entries</span>
                <strong>{selectedBatch.valid_entries || 0}</strong>
              </div>
              <div className="analytics-card">
                <span>Range</span>
                <strong>
                  {formatRange(
                    selectedBatch.earliest_play,
                    selectedBatch.latest_play
                  )}
                </strong>
              </div>
            </div>
            <div className="analytics-sections">
              {!loading && error && (
                <div className="analytics-section analytics-section--loading">{error}</div>
              )}
              {!loading && !error && (
                <>
                  {visibleAnalyticsSections.map((section, index) => (
                    <div
                      key={section.key}
                      className="analytics-section analytics-section--reveal"
                      style={{ "--analytics-reveal-index": index }}
                    >
                      {section.content}
                    </div>
                  ))}
                </>
              )}
              {loading && (
                <div className="analytics-section analytics-section--loading">
                  Loading charts
                  <span className="analytics-loading-dots" aria-hidden="true" />
                </div>
              )}
            </div>
            {showAnalyticsScrollPrompt && (
              <div className="hub-scroll-indicator" aria-hidden="true">
                <div className="hub-scroll-indicator-text">SCROLL</div>
                <img
                  className="hub-scroll-indicator-arrow"
                  src={`${process.env.PUBLIC_URL}/assets/scroll-arrow.png`}
                  alt=""
                />
              </div>
            )}
          </>
        ) : (
          <div className="generated-playlists generated-playlists--empty">
            No batches available yet.
          </div>
        )}
        </div>
      </div>
      <ConfirmModal
        isOpen={Boolean(premiumFeaturePrompt)}
        title="PREMIUM FEATURE"
        message={premiumFeaturePrompt}
        confirmText="VIEW PREMIUM"
        cancelText="NOT NOW"
        onConfirm={() => {
          setPremiumFeaturePrompt("");
          onOpenPremium?.();
        }}
        onCancel={() => setPremiumFeaturePrompt("")}
        variant="warning"
        showCancel
      />
    </>
  );
}

function AppContent({
  token,
  appUserId,
  paletteId,
  onPaletteChange,
  onLogout,
  onStartPremiumCheckout,
  onOpenBillingPortal,
  userProfile,
  billingStatus,
  activeScreen,
  historyMode,
  hasUploadedHistory,
  modeLoading,
  onSelectMode,
  onUploadComplete,
  onOpenDataHub,
  onOpenHowTo,
  onOpenLibrary,
  onOpenLegal,
  onOpenPremium,
  onOpenSignIn,
  onOpenWaitlist,
  onContinueSpotifyLogin,
  onEnterRadio,
  onEnterRadioLocal,
  onRadioTransitionComplete,
  onSelectImportBatch,
  onDeleteImportBatch,
  onRenameImportBatch,
  onOpenBatchAnalytics,
  analyticsBatchId,
  onSelectAnalyticsBatch,
  onBackToHub,
  onEnterRadioFromAnalytics,
  importBatches,
  playlistsLoading,
  selectedImportId,
  onGoHome,
  radioLaunchContext,
}) {
  const isAuthenticated = Boolean(token) || Boolean(appUserId);
  const supportsDataHub = isAuthenticated;
  const supportsHowTo = isAuthenticated;
  const effectiveScreen =
    !isAuthenticated &&
    (activeScreen === "hub" || activeScreen === "analytics" || activeScreen === "onboarding")
      ? "home"
      : activeScreen;
  const selectedImportBatch =
    importBatches.find((batch) => batch.id === selectedImportId) || null;
  const activeBatchLabel = selectedImportBatch
    ? selectedImportBatch.custom_name || `Batch #${selectedImportBatch.id}`
    : "";
  const appClass = !isAuthenticated
    ? "app--login"
    : effectiveScreen === "player"
    ? "app--player"
    : effectiveScreen === "radio-transition"
    ? "app--transition"
    : effectiveScreen === "home"
    ? "app--login"
    : "app--hub";
  const navModeLabel = isAuthenticated ? "SPOTIFY" : "";
  return (
    <div className={`App ${appClass}`}>
      {appClass !== "app--player" && appClass !== "app--transition" && (
        <AppCrtOverlay />
      )}
      {!isAuthenticated && effectiveScreen !== "legal" ? (
        <>
          <UtilitiesNav
            onOpenLegal={onOpenLegal}
            onGoHome={onGoHome}
            onOpenSignIn={onOpenSignIn}
            onOpenWaitlist={onOpenWaitlist}
            onOpenLibrary={onOpenLibrary}
            supportsDataHub={supportsDataHub}
            supportsHowTo={supportsHowTo}
            supportsLibrary={isAuthenticated}
            modeLabel={navModeLabel}
          />
          {effectiveScreen === "signin" ? (
            <Login
              onOpenWaitlist={onOpenWaitlist}
              onContinueSpotifyLogin={onContinueSpotifyLogin}
            />
          ) : (
            <HomeLanding onOpenSignIn={onOpenSignIn} />
          )}
        </>
      ) : effectiveScreen === "home" ? (
        <>
          <UtilitiesNav
            authenticated
            onLogout={onLogout}
            onGetPremium={onOpenPremium}
            onManageBilling={onOpenBillingPortal}
            userProfile={userProfile}
            billingStatus={billingStatus}
            onOpenHowTo={supportsHowTo ? onOpenHowTo : undefined}
            onOpenDataHub={supportsDataHub ? onOpenDataHub : undefined}
            onOpenLibrary={onOpenLibrary}
            onOpenLegal={onOpenLegal}
            onGoHome={onGoHome}
            activeUtility="home"
            supportsDataHub={supportsDataHub}
            supportsHowTo={supportsHowTo}
            supportsLibrary={isAuthenticated}
            modeLabel={navModeLabel}
          />
          <HomeLanding
            onOpenSignIn={isAuthenticated ? onOpenDataHub : onOpenSignIn}
          />
        </>
      ) : effectiveScreen === "hub" ? (
        <>
          <UtilitiesNav
            authenticated
            onLogout={onLogout}
            onGetPremium={onOpenPremium}
            onManageBilling={onOpenBillingPortal}
            userProfile={userProfile}
            billingStatus={billingStatus}
            onOpenHowTo={supportsHowTo ? onOpenHowTo : undefined}
            onOpenDataHub={supportsDataHub ? onOpenDataHub : undefined}
            onOpenLibrary={onOpenLibrary}
            onOpenLegal={onOpenLegal}
            onGoHome={onGoHome}
            activeUtility="hub"
            supportsDataHub={supportsDataHub}
            supportsHowTo={supportsHowTo}
            supportsLibrary={isAuthenticated}
            modeLabel={navModeLabel}
          />
          <DataHub
            historyMode={historyMode}
            hasUploadedHistory={hasUploadedHistory}
            modeLoading={modeLoading}
            onSelectMode={onSelectMode}
            onUploadComplete={onUploadComplete}
            onEnterRadio={onEnterRadio}
            onSelectImportBatch={onSelectImportBatch}
            onDeleteImportBatch={onDeleteImportBatch}
            onRenameImportBatch={onRenameImportBatch}
            onOpenBatchAnalytics={onOpenBatchAnalytics}
            importBatches={importBatches}
            playlistsLoading={playlistsLoading}
            selectedImportId={selectedImportId}
          />
        </>
      ) : effectiveScreen === "onboarding" ? (
        <>
          <UtilitiesNav
            authenticated
            onLogout={onLogout}
            onGetPremium={onOpenPremium}
            onManageBilling={onOpenBillingPortal}
            userProfile={userProfile}
            billingStatus={billingStatus}
            onOpenHowTo={supportsHowTo ? onOpenHowTo : undefined}
            onOpenDataHub={supportsDataHub ? onOpenDataHub : undefined}
            onOpenLibrary={onOpenLibrary}
            onOpenLegal={onOpenLegal}
            onGoHome={onGoHome}
            activeUtility="howto"
            supportsDataHub={supportsDataHub}
            supportsHowTo={supportsHowTo}
            supportsLibrary={isAuthenticated}
            modeLabel={navModeLabel}
          />
          <OnboardingWizard
            onOpenWaitlist={onOpenWaitlist}
            onContinueSpotifyLogin={onContinueSpotifyLogin}
          />
        </>
      ) : effectiveScreen === "local-onboarding" ? (
        <>
          <UtilitiesNav
            authenticated
            onLogout={onLogout}
            onGetPremium={onOpenPremium}
            onManageBilling={onOpenBillingPortal}
            userProfile={userProfile}
            billingStatus={billingStatus}
            onOpenHowTo={supportsHowTo ? onOpenHowTo : undefined}
            onOpenDataHub={supportsDataHub ? onOpenDataHub : undefined}
            onOpenLibrary={onOpenLibrary}
            onOpenLegal={onOpenLegal}
            onGoHome={onGoHome}
            activeUtility="howto"
            supportsDataHub={supportsDataHub}
            supportsHowTo={supportsHowTo}
            supportsLibrary={isAuthenticated}
            modeLabel={navModeLabel}
          />
          <LocalOnboardingWizard onLaunchRadio={onEnterRadioLocal} />
        </>
      ) : effectiveScreen === "legal" ? (
        <>
          <UtilitiesNav
            authenticated={isAuthenticated}
            onLogout={onLogout}
            onGetPremium={onOpenPremium}
            onManageBilling={onOpenBillingPortal}
            userProfile={userProfile}
            billingStatus={billingStatus}
            onOpenHowTo={supportsHowTo ? onOpenHowTo : undefined}
            onOpenDataHub={supportsDataHub ? onOpenDataHub : undefined}
            onOpenLibrary={onOpenLibrary}
            onOpenLegal={onOpenLegal}
            onGoHome={onGoHome}
            activeUtility="legal"
            supportsDataHub={supportsDataHub}
            supportsHowTo={supportsHowTo}
            supportsLibrary={isAuthenticated}
            modeLabel={navModeLabel}
          />
          <LegalCenter
            onBack={isAuthenticated ? onOpenDataHub : onGoHome}
          />
        </>
      ) : effectiveScreen === "premium" ? (
        <>
          <UtilitiesNav
            authenticated
            onLogout={onLogout}
            onGetPremium={onOpenPremium}
            onManageBilling={onOpenBillingPortal}
            userProfile={userProfile}
            billingStatus={billingStatus}
            onOpenHowTo={supportsHowTo ? onOpenHowTo : undefined}
            onOpenDataHub={supportsDataHub ? onOpenDataHub : undefined}
            onOpenLibrary={onOpenLibrary}
            onOpenLegal={onOpenLegal}
            onGoHome={onGoHome}
            supportsDataHub={supportsDataHub}
            supportsHowTo={supportsHowTo}
            supportsLibrary={isAuthenticated}
            modeLabel={navModeLabel}
          />
          <PremiumPlans
            onStartCheckout={onStartPremiumCheckout}
            onManageBilling={onOpenBillingPortal}
            billingStatus={billingStatus}
            onBack={supportsDataHub ? onOpenDataHub : onGoHome}
          />
        </>
      ) : effectiveScreen === "analytics" ? (
        <BatchAnalytics
          importBatches={importBatches}
          analyticsBatchId={analyticsBatchId}
          onSelectBatch={onSelectAnalyticsBatch}
          onBackToHub={onBackToHub}
          onEnterRadioFromAnalytics={onEnterRadioFromAnalytics}
          billingStatus={billingStatus}
          onOpenPremium={onOpenPremium}
        />
      ) : effectiveScreen === "library" ? (
        <>
          <UtilitiesNav
            authenticated
            onLogout={onLogout}
            onGetPremium={onOpenPremium}
            onManageBilling={onOpenBillingPortal}
            userProfile={userProfile}
            billingStatus={billingStatus}
            onOpenHowTo={supportsHowTo ? onOpenHowTo : undefined}
            onOpenDataHub={supportsDataHub ? onOpenDataHub : undefined}
            onOpenLibrary={onOpenLibrary}
            onOpenLegal={onOpenLegal}
            onGoHome={onGoHome}
            activeUtility="library"
            supportsDataHub={supportsDataHub}
            supportsHowTo={supportsHowTo}
            supportsLibrary={isAuthenticated}
            modeLabel={navModeLabel}
          />
          <LocalLibraryEditor
            onBack={onGoHome}
            onLaunchRadio={onEnterRadioLocal}
          />
        </>
      ) : effectiveScreen === "radio-transition" ? (
        <RadioTransitionScreen onComplete={onRadioTransitionComplete} />
      ) : (
        <VintagePlayer
          paletteId={paletteId}
          paletteOptions={RADIO_PALETTES}
          onPaletteChange={onPaletteChange}
          billingStatus={billingStatus}
          onOpenPremium={onOpenPremium}
          hasUploadedHistory={hasUploadedHistory}
          activeHistoryMode={historyMode}
          onOpenDataHub={onOpenDataHub}
          onOpenLibrary={onOpenLibrary}
          activeBatchId={selectedImportId}
          activeBatchLabel={activeBatchLabel}
          radioLaunchContext={radioLaunchContext}
        />
      )}
      {appClass !== "app--player" && appClass !== "app--transition" && (
        <SiteLegalFooter onOpenLegal={onOpenLegal} />
      )}
    </div>
  );
}

function App() {
  const [token, setToken] = useState("");
  const [appUserId, setAppUserId] = useState(null);
  const [paletteId, setPaletteId] = useState(DEFAULT_RADIO_PALETTE_ID);
  const [historyMode, setHistoryMode] = useState("");
  const [modeLoading, setModeLoading] = useState(false);
  const [hasUploadedHistory, setHasUploadedHistory] = useState(false);
  const [activeScreen, setActiveScreen] = useState("hub");
  const [radioLaunchContext, setRadioLaunchContext] = useState("spotify");
  const [importBatches, setImportBatches] = useState([]);
  const [selectedImportId, setSelectedImportId] = useState(null);
  const [analyticsBatchId, setAnalyticsBatchId] = useState(null);
  const [playlistsLoading, setPlaylistsLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [billingStatus, setBillingStatus] = useState(EMPTY_BILLING_STATUS);
  const [checkoutWarning, setCheckoutWarning] = useState("");
  const [networkWarning, setNetworkWarning] = useState("");
  const [premiumSuccessNotice, setPremiumSuccessNotice] = useState("");
  const [welcomeNotice, setWelcomeNotice] = useState("");
  const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistDisplayName, setWaitlistDisplayName] = useState("");
  const [waitlistNote, setWaitlistNote] = useState("");
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistError, setWaitlistError] = useState("");
  const [waitlistSuccess, setWaitlistSuccess] = useState("");
  const authRedirectInFlightRef = useRef(false);

  const fetchBillingStatus = useCallback(
    async ({ signal } = {}) => {
      if (!appUserId) {
        setBillingStatus(EMPTY_BILLING_STATUS);
        return EMPTY_BILLING_STATUS;
      }

      const response = await fetch(`${API_ORIGIN}/api/billing/status`, {
        credentials: "include",
        signal,
      });
      if (!response.ok) {
        throw new Error("Failed to fetch billing status");
      }
      const billing = await response.json();
      const nextBilling = {
        is_premium: Boolean(billing?.is_premium),
        subscription_status: billing?.subscription_status || null,
        premium_until: billing?.premium_until || null,
        current_period_start: billing?.current_period_start || null,
        cancel_at: billing?.cancel_at || null,
        cancel_at_period_end:
          typeof billing?.cancel_at_period_end === "boolean"
            ? billing.cancel_at_period_end
            : null,
        stripe_price_id: billing?.stripe_price_id || null,
        stripe_customer_id: billing?.stripe_customer_id || null,
      };
      setBillingStatus(nextBilling);
      return nextBilling;
    },
    [appUserId]
  );

  const reconcileBillingStatus = useCallback(async () => {
    if (!appUserId) return EMPTY_BILLING_STATUS;
    const response = await fetch(`${API_ORIGIN}/api/billing/reconcile`, {
      method: "POST",
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error("Failed to reconcile billing status");
    }
    const billing = await response.json();
    const nextBilling = {
      is_premium: Boolean(billing?.is_premium),
      subscription_status: billing?.subscription_status || null,
      premium_until: billing?.premium_until || null,
      current_period_start: billing?.current_period_start || null,
      cancel_at: billing?.cancel_at || null,
      cancel_at_period_end:
        typeof billing?.cancel_at_period_end === "boolean"
          ? billing.cancel_at_period_end
          : null,
      stripe_price_id: billing?.stripe_price_id || null,
      stripe_customer_id: billing?.stripe_customer_id || null,
    };
    setBillingStatus(nextBilling);
    return nextBilling;
  }, [appUserId]);

  const clearClientSessionState = useCallback(() => {
    setToken("");
    setAppUserId(null);
    setHistoryMode("");
    setHasUploadedHistory(false);
    setActiveScreen("hub");
    setImportBatches([]);
    setSelectedImportId(null);
    setAnalyticsBatchId(null);
    setUserProfile(null);
    setBillingStatus(EMPTY_BILLING_STATUS);
    setCheckoutWarning("");
    setNetworkWarning("");
    setPremiumSuccessNotice("");
    setWelcomeNotice("");
    setIsWaitlistModalOpen(false);
    setWaitlistSubmitting(false);
    setWaitlistError("");
    setWaitlistSuccess("");
    setRadioLaunchContext("spotify");
  }, []);

  const continueSpotifyLogin = useCallback(() => {
    if (authRedirectInFlightRef.current) return;
    authRedirectInFlightRef.current = true;
    window.location.assign(`${API_ORIGIN}/auth/login`);
  }, []);

  const fetchAuthSession = useCallback(async () => {
    try {
      const response = await fetch(`${API_ORIGIN}/auth/session`, {
        credentials: "include",
        cache: "no-store",
      });
      if (!response.ok) {
        throw new Error("Failed to fetch auth session");
      }
      const data = await response.json();
      setAppUserId(Number(data?.app_user_id || 0) || null);
      if (data?.display_name) {
        setUserProfile({
          display_name: data.display_name,
          image_url: "",
        });
      }
      return data;
    } catch {
      setAppUserId(null);
      setUserProfile(null);
      return null;
    }
  }, []);

  const openWaitlistModal = useCallback(() => {
    setWaitlistError("");
    setWaitlistSuccess("");
    setIsWaitlistModalOpen(true);
  }, []);

  const closeWaitlistModal = useCallback(() => {
    if (waitlistSubmitting) return;
    setIsWaitlistModalOpen(false);
  }, [waitlistSubmitting]);

  const submitWaitlistSignup = useCallback(async () => {
    const normalizedEmail = String(waitlistEmail || "").trim().toLowerCase();
    const normalizedName = String(waitlistDisplayName || "").trim();
    if (!normalizedName) {
      setWaitlistError("Enter your name.");
      return;
    }
    if (!normalizedEmail) {
      setWaitlistError("Enter your Spotify account email.");
      return;
    }

    setWaitlistSubmitting(true);
    setWaitlistError("");
    setWaitlistSuccess("");
    try {
      const response = await fetch(`${API_ORIGIN}/api/waitlist/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: normalizedEmail,
          display_name: normalizedName,
          note: String(waitlistNote || "").trim(),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const errorCode = payload?.error || "WAITLIST_SIGNUP_FAILED";
        throw new Error(errorCode);
      }

      setWaitlistSuccess(
        payload?.already_registered
          ? "You are already on the waitlist. We will email you once approved."
          : "You are on the waitlist. We will review and approve access."
      );
      setWaitlistDisplayName("");
      setWaitlistNote("");
    } catch (error) {
      if (String(error?.message || "").includes("INVALID_EMAIL")) {
        setWaitlistError("Enter a valid email address.");
      } else if (String(error?.message || "").includes("MISSING_NAME")) {
        setWaitlistError("Enter your name.");
      } else {
        setWaitlistError("Could not submit waitlist request. Try again.");
      }
    } finally {
      setWaitlistSubmitting(false);
    }
  }, [waitlistEmail, waitlistDisplayName, waitlistNote]);

  const fetchSessionToken = useCallback(
    async ({ redirectOnUnauthorized = false } = {}) => {
      try {
        const response = await fetch(`${API_ORIGIN}/auth/token`, {
          credentials: "include",
          cache: "no-store",
        });
        if (response.status === 401) {
          clearClientSessionState();
          if (redirectOnUnauthorized) {
            continueSpotifyLogin();
          }
          return "";
        }
        if (!response.ok) {
          throw new Error("Failed to fetch auth token");
        }
      const data = await response.json();
      const nextToken = data.access_token || "";
      setAppUserId(Number(data?.app_user_id || 0) || null);
      setToken(nextToken);
      return nextToken;
      } catch {
        clearClientSessionState();
        return "";
      }
    },
    [clearClientSessionState, continueSpotifyLogin]
  );

  const refreshImportBatches = async () => {
    if (!token) {
      setImportBatches([]);
      return;
    }

    setPlaylistsLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/history/imports`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to fetch imports");
      }
      const data = await response.json();
      setImportBatches(Array.isArray(data.imports) ? data.imports : []);
    } catch (error) {
      setImportBatches([]);
    } finally {
      setPlaylistsLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const bootstrapSession = async () => {
      const session = await fetchAuthSession();
      if (cancelled) return;
      if (session?.spotify_connected || session?.app_user_id) {
        await fetchSessionToken({ redirectOnUnauthorized: false });
        setRadioLaunchContext("spotify");
      } else {
        setToken("");
      }
    };
    bootstrapSession();
    return () => {
      cancelled = true;
    };
  }, [fetchAuthSession, fetchSessionToken]);

  useEffect(() => {
    if (!token) return undefined;
    const interval = window.setInterval(() => {
      fetchSessionToken({ redirectOnUnauthorized: false });
    }, 4 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, [token, fetchSessionToken]);

  useEffect(() => {
    const storedPaletteId = window.localStorage.getItem(PALETTE_STORAGE_KEY);
    if (!storedPaletteId) {
      setPaletteId(applyRadioPalette(DEFAULT_RADIO_PALETTE_ID));
      return;
    }
    setPaletteId(applyRadioPalette(storedPaletteId));
  }, []);

  const handlePaletteChange = (nextPaletteId, { persist = true } = {}) => {
    const appliedId = applyRadioPalette(nextPaletteId);
    setPaletteId(appliedId);
    if (persist) {
      window.localStorage.setItem(PALETTE_STORAGE_KEY, appliedId);
    }
  };

  const handleLogout = () => {
    fetch(`${API_ORIGIN}/auth/logout`, {
      method: "POST",
      credentials: "include",
      cache: "no-store",
    })
      .catch(() => undefined)
      .finally(() => {
        clearClientSessionState();
      });
  };

  const handleGetPremium = async (plan = "monthly") => {
    try {
      const response = await fetch(`${API_ORIGIN}/api/billing/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ plan }),
      });
      if (!response.ok) {
        let errorCode = "CHECKOUT_SESSION_REQUEST_FAILED";
        let stripeMessage = "";
        try {
          const payload = await response.json();
          errorCode = payload?.error || errorCode;
          stripeMessage = payload?.stripe_error?.message || "";
        } catch (_parseError) {
          // noop
        }
        throw new Error(stripeMessage ? `${errorCode}: ${stripeMessage}` : errorCode);
      }
      const data = await response.json();
      if (data?.checkout_url) {
        window.location.assign(data.checkout_url);
      }
    } catch (error) {
      setCheckoutWarning(
        `Unable to start Stripe checkout. ${error?.message || "Please try again."}`
      );
    }
  };

  const handleOpenBillingPortal = async () => {
    try {
      const response = await fetch(`${API_ORIGIN}/api/billing/create-portal-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          return_url: `${window.location.origin}/premium`,
        }),
      });
      if (!response.ok) {
        let errorCode = "PORTAL_SESSION_REQUEST_FAILED";
        try {
          const payload = await response.json();
          errorCode = payload?.error || errorCode;
        } catch (_parseError) {
          // noop
        }
        throw new Error(errorCode);
      }
      const data = await response.json();
      if (data?.portal_url) {
        window.location.assign(data.portal_url);
        return;
      }
      throw new Error("PORTAL_URL_MISSING");
    } catch (error) {
      setNetworkWarning(
        `Unable to open billing portal. ${error?.message || "Please try again."}`
      );
    }
  };

  useEffect(() => {
    if (!token) return;

    fetch(`${API_ORIGIN}/api/history/status`, { credentials: "include" })
      .then((response) => response.json())
      .then((data) => {
        if (data.hasUploadedData) {
          setHasUploadedHistory(true);
          setSelectedImportId(data.selectedImportId || null);
          setAnalyticsBatchId(data.selectedImportId || null);
          if (data.mode === "yearly-hits") {
            setHistoryMode("yearly-hits");
            setSelectedImportId(null);
            setAnalyticsBatchId(null);
            setActiveScreen("hub");
            return;
          }
          setHistoryMode("time-travel-ready");
          setActiveScreen("hub");
          return;
        }
        setHasUploadedHistory(false);
        setSelectedImportId(null);
        setAnalyticsBatchId(null);
        if (data.mode === "yearly-hits") {
          setHistoryMode("yearly-hits");
          setActiveScreen("hub");
          return;
        }
        setHistoryMode("");
        setActiveScreen("hub");
      })
      .catch(() => {
        setHistoryMode("");
        setActiveScreen("hub");
        setSelectedImportId(null);
        setAnalyticsBatchId(null);
        setNetworkWarning(
          "Could not reach the server to load your history state. Please check your connection."
        );
      });
  }, [token]);

  useEffect(() => {
    refreshImportBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!appUserId) {
      setUserProfile(null);
      setBillingStatus(EMPTY_BILLING_STATUS);
      return;
    }

    const controller = new AbortController();
    const fetchProfileAndBilling = async () => {
      try {
        const [profileResult, billingResult] = await Promise.allSettled([
          token
            ? fetch("https://api.spotify.com/v1/me", {
                headers: { Authorization: `Bearer ${token}` },
                signal: controller.signal,
              })
            : Promise.resolve(null),
          fetchBillingStatus({ signal: controller.signal }),
        ]);

        if (token) {
          if (profileResult.status === "fulfilled" && profileResult.value?.ok) {
            const profile = await profileResult.value.json();
            const imageUrl = Array.isArray(profile?.images) ? profile.images[0]?.url || "" : "";
            setUserProfile({
              display_name: profile?.display_name || "Spotify User",
              image_url: imageUrl,
            });
          } else {
            setUserProfile(null);
          }
        }

        if (billingResult.status !== "fulfilled") {
          setBillingStatus(EMPTY_BILLING_STATUS);
        }
      } catch (_error) {
        setUserProfile(null);
        setBillingStatus(EMPTY_BILLING_STATUS);
      }
    };

    fetchProfileAndBilling();
    return () => controller.abort();
  }, [appUserId, token, fetchBillingStatus]);

  useEffect(() => {
    if (!appUserId) return undefined;

    const pathname = window.location.pathname || "";
    const isBillingSuccess = pathname === "/billing/success";
    if (!isBillingSuccess) return undefined;

    let cancelled = false;
    let attempt = 0;
    const maxAttempts = 12;

    const pollBillingStatus = async () => {
      let premiumActivated = false;
      while (!cancelled && attempt < maxAttempts) {
        attempt += 1;
        try {
          await reconcileBillingStatus();
          const billing = await fetchBillingStatus();
          if (billing.is_premium) {
            premiumActivated = true;
            break;
          }
        } catch (_error) {
          // noop: keep polling for webhook propagation
        }
        await new Promise((resolve) => window.setTimeout(resolve, 1500));
      }

      if (!cancelled) {
        if (premiumActivated) {
          setPremiumSuccessNotice(
            "Premium is now active!"
          );
        }
        window.history.replaceState({}, "", "/");
      }
    };

    pollBillingStatus();
    return () => {
      cancelled = true;
    };
  }, [appUserId, fetchBillingStatus, reconcileBillingStatus]);

  const handleSelectMode = async (mode) => {
    setModeLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/history/mode`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ mode }),
      });
      if (!response.ok) throw new Error("Failed to set mode");
      if (mode === "yearly-hits") {
        setHistoryMode("yearly-hits");
        setSelectedImportId(null);
        setAnalyticsBatchId(null);
      } else {
        setHistoryMode("time-travel-ready");
      }
    } catch (error) {
      setHistoryMode("");
      setNetworkWarning(
        "Could not update listening mode. Check your connection and try again."
      );
    } finally {
      setModeLoading(false);
    }
  };

  const handleUploadComplete = (data) => {
    setHasUploadedHistory(true);
    setHistoryMode("time-travel-ready");
    setSelectedImportId(data?.importId || null);
    setAnalyticsBatchId(data?.importId || null);
    refreshImportBatches();
  };

  const handleEnterRadio = () => {
    if (!historyMode) return;
    setRadioLaunchContext("spotify");
    setActiveScreen("radio-transition");
  };

  const handleEnterLocalRadio = () => {
    setRadioLaunchContext("local");
    setActiveScreen("radio-transition");
  };

  const handleSelectImportBatch = async (importId) => {
    try {
      const response = await fetch(
        `${API_ORIGIN}/api/history/imports/${importId}/select`,
        {
          method: "POST",
          credentials: "include",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to select import batch");
      }
      setHistoryMode("time-travel-ready");
      setHasUploadedHistory(true);
      setSelectedImportId(importId);
      setAnalyticsBatchId(importId);
    } catch (error) {
      setNetworkWarning(
        "Could not select that batch right now. Please reconnect and try again."
      );
    }
  };

  const handleDeleteImportBatch = async (importId) => {
    const response = await fetch(
      `${API_ORIGIN}/api/history/imports/${importId}`,
      {
        method: "DELETE",
        credentials: "include",
      }
    );
    if (!response.ok) {
      throw new Error("Failed to delete import batch");
    }

    const data = await response.json();
    const stillHasUploads = Boolean(data.hasUploadedData);
    setHasUploadedHistory(stillHasUploads);
    setSelectedImportId(data.selectedImportId || null);
    setAnalyticsBatchId(data.selectedImportId || null);
    if (!stillHasUploads && historyMode === "time-travel-ready") {
      setHistoryMode("");
    }
    refreshImportBatches();
  };

  const handleRenameImportBatch = async (importId, name) => {
    const response = await fetch(
      `${API_ORIGIN}/api/history/imports/${importId}`,
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      }
    );
    if (!response.ok) {
      throw new Error("Failed to rename import batch");
    }

    setImportBatches((prev) =>
      prev.map((batch) =>
        batch.id === importId ? { ...batch, custom_name: name } : batch
      )
    );
  };

  const handleOpenBatchAnalytics = (importId) => {
    setAnalyticsBatchId(importId);
    setActiveScreen("analytics");
  };

  const handleEnterRadioFromAnalytics = async (importId) => {
    try {
      const response = await fetch(
        `${API_ORIGIN}/api/history/imports/${importId}/select`,
        {
          method: "POST",
          credentials: "include",
        }
      );
      if (!response.ok) throw new Error("Failed to select import batch");
      setHistoryMode("time-travel-ready");
      setHasUploadedHistory(true);
      setSelectedImportId(importId);
      setAnalyticsBatchId(importId);
      setRadioLaunchContext("spotify");
      setActiveScreen("radio-transition");
    } catch {
      setNetworkWarning(
        "Could not launch radio from analytics. Check connection and retry."
      );
    }
  };

  return (
    <>
      <PlayerProvider
        key={`player:${appUserId || "anon"}:${token ? "spotify" : "none"}`}
        accessToken={token}
        onAuthExpired={continueSpotifyLogin}
      >
        <HistoryProvider
          key={`history:${token}:${historyMode}:${selectedImportId || "none"}`}
        >
          <AppContent
            token={token}
            appUserId={appUserId}
            paletteId={paletteId}
            onPaletteChange={handlePaletteChange}
            onLogout={handleLogout}
            onStartPremiumCheckout={handleGetPremium}
            onOpenBillingPortal={handleOpenBillingPortal}
            userProfile={userProfile}
            billingStatus={billingStatus}
            activeScreen={activeScreen}
            historyMode={historyMode}
            hasUploadedHistory={hasUploadedHistory}
            modeLoading={modeLoading}
            onSelectMode={handleSelectMode}
            onUploadComplete={handleUploadComplete}
            onOpenDataHub={() => setActiveScreen("hub")}
            onOpenHowTo={() => setActiveScreen("onboarding")}
            onOpenLibrary={() => setActiveScreen("library")}
            onOpenLegal={() => setActiveScreen("legal")}
            onOpenPremium={() => setActiveScreen("premium")}
            onOpenSignIn={() => setActiveScreen("signin")}
            onGoHome={() => setActiveScreen("home")}
            onOpenWaitlist={openWaitlistModal}
            onContinueSpotifyLogin={continueSpotifyLogin}
            onEnterRadio={handleEnterRadio}
            onEnterRadioLocal={handleEnterLocalRadio}
            onRadioTransitionComplete={() => setActiveScreen("player")}
            onSelectImportBatch={handleSelectImportBatch}
            onDeleteImportBatch={handleDeleteImportBatch}
            onRenameImportBatch={handleRenameImportBatch}
            onOpenBatchAnalytics={handleOpenBatchAnalytics}
            analyticsBatchId={analyticsBatchId}
            onSelectAnalyticsBatch={setAnalyticsBatchId}
            onBackToHub={() => setActiveScreen("hub")}
            onEnterRadioFromAnalytics={handleEnterRadioFromAnalytics}
            importBatches={importBatches}
            playlistsLoading={playlistsLoading}
            selectedImportId={selectedImportId}
            radioLaunchContext={radioLaunchContext}
          />
        </HistoryProvider>
      </PlayerProvider>
      <WaitlistModal
        isOpen={isWaitlistModalOpen}
        email={waitlistEmail}
        displayName={waitlistDisplayName}
        note={waitlistNote}
        onChangeEmail={setWaitlistEmail}
        onChangeDisplayName={setWaitlistDisplayName}
        onChangeNote={setWaitlistNote}
        onSubmit={submitWaitlistSignup}
        onClose={closeWaitlistModal}
        onContinueSpotifyLogin={continueSpotifyLogin}
        isSubmitting={waitlistSubmitting}
        error={waitlistError}
        success={waitlistSuccess}
      />
      <ConfirmModal
        isOpen={Boolean(checkoutWarning)}
        title="CHECKOUT UNAVAILABLE"
        message={checkoutWarning}
        confirmText="OK"
        onConfirm={() => setCheckoutWarning("")}
        onCancel={() => setCheckoutWarning("")}
        showCancel={false}
        variant="warning"
      />
      <ConfirmModal
        isOpen={Boolean(networkWarning)}
        title="CONNECTION ISSUE"
        message={networkWarning}
        confirmText="RETRY LATER"
        onConfirm={() => setNetworkWarning("")}
        onCancel={() => setNetworkWarning("")}
        showCancel={false}
        variant="warning"
      />
      <ConfirmModal
        isOpen={Boolean(premiumSuccessNotice)}
        title="PREMIUM UNLOCKED"
        message={premiumSuccessNotice}
        confirmText="PROCEED"
        onConfirm={() => setPremiumSuccessNotice("")}
        onCancel={() => setPremiumSuccessNotice("")}
        showCancel={false}
        variant="premium-success"
      />
      <ConfirmModal
        isOpen={Boolean(welcomeNotice)}
        title="WELCOME"
        message={welcomeNotice}
        confirmText="CONTINUE"
        onConfirm={() => setWelcomeNotice("")}
        onCancel={() => setWelcomeNotice("")}
        showCancel={false}
      />
    </>
  );
}

export default App;
